import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setAppointmentData } from "../../redux/slices/prescriptionSlice";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import SaveIcon from "@mui/icons-material/Save";
import { batchUpsertClinicalData } from "../../connections/postApi";
import { getStaticDataList, getClinicalData } from "../../connections/getApi";
import { updatePatientAppointment } from "../../connections/putApi";
import { showToast } from "../../utils/toastUtil";
import {
  primaryBtnTeal,
  selectStyles,
  textfieldTextPrep,
} from "../../style/PatientDetailsStyle";

// ─── Shared styles ────────────────────────────────────────────────────────────
const fieldBase = {
  width: "100%",
  height: "32px",
  border: "1px solid #D0D8DA",
  borderRadius: "6px",
  fontSize: "14px",
  padding: "0 8px",
  backgroundColor: "white",
  color: "#333",
  outline: "none",
  fontFamily: "Lato, sans-serif",
  boxSizing: "border-box",
};
const labelStyle = {
  fontSize: "14px",
  fontWeight: 700,
  color: "#666",
  textTransform: "uppercase",
  marginBottom: "4px",
  display: "block",
};
const allergySelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "14px",
    borderRadius: "6px",
    borderColor: state.isFocused ? "#0097B2" : "#D0D8DA",
    boxShadow: state.isFocused ? "0 0 0 1px #0097B2" : "none",
    "&:hover": { borderColor: "#0097B2" },
    fontFamily: "Lato, sans-serif",
    backgroundColor: "white",
    cursor: "pointer",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "14px" }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "0 4px",
    color: "#0097B2",
  }),
  clearIndicator: (base) => ({ ...base, padding: "0 4px" }),
  singleValue: (base) => ({ ...base, fontSize: "14px", color: "#333" }),
  placeholder: (base) => ({ ...base, fontSize: "14px", color: "#999" }),
  menu: (base) => ({
    ...base,
    fontSize: "14px",
    zIndex: 9999,
    fontFamily: "Lato, sans-serif",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "14px",
    padding: "8px 10px",
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? "#0097B2"
      : state.isFocused
        ? "#e6f7fa"
        : "#fff",
    color: state.isSelected ? "#fff" : "#333",
  }),
};

// ─── Allergy constants ────────────────────────────────────────────────────────
const ALLERGY_TYPE_OPTIONS = [
  { label: "Medication", value: "Medication" },
  { label: "Other", value: "Other" },
];
const ALLERGY_AGENT_OPTIONS = [
  "Aceclofenac",
  "Acetaaminophen",
  "Acotiamide",
  "Allopurinol",
  "Amitryptyline",
  "Amlodipine",
  "Amoxycillin",
  "Ampicillin",
  "Aspirin",
  "Augmentin",
  "Azathioprine",
  "Azithromycin",
  "B complex",
  "Cetirizine",
  "Ciprofloxacin",
  "Clopidogrel",
  "Codeine",
  "Dexamethasone",
  "Diclofenac",
  "Doxycycline",
  "Erythromycin",
  "Fluconazole",
  "Ibuprofen",
  "Insulin",
  "Levofloxacin",
  "Metformin",
  "Methotrexate",
  "Metronidazole",
  "Morphine",
  "Naproxen",
  "Paracetamol",
  "Penicillin",
  "Prednisolone",
  "Ranitidine",
  "Rifampicin",
  "Salbutamol",
  "Tetracycline",
  "Tramadol",
  "Warfarin",
].map((a) => ({ label: a, value: a }));
const ALLERGY_REACTION_OPTIONS = [
  "Abdominal bloating",
  "Anaphylaxis",
  "Diarrhoea",
  "Dizziness",
  "Edema",
  "Hives",
  "Itching",
  "Nausea",
  "Rash",
  "Shortness of breath",
  "Skin rash",
  "Swelling",
  "Urticaria",
  "Vomiting",
].map((r) => ({ label: r, value: r }));
const ALLERGY_SEVERITY_OPTIONS = [
  "Mild",
  "Mild to moderate",
  "Moderate",
  "Moderate to severe",
  "Severe",
  "Fatal",
  "Life threatening",
].map((s) => ({ label: s, value: s }));
const emptyAllergyRow = () => ({
  type: ALLERGY_TYPE_OPTIONS[0],
  agent: null,
  reactions: null,
  severity: null,
  startDate: "",
  comments: "",
});

const AllergyRow = ({ data, onChange, disabled = false }) => {
  const set = (field) => (val) => onChange({ ...data, [field]: val });
  return (
    <Box
      sx={{
        border: "1px solid #E1E1E1",
        borderRadius: "8px",
        p: 1,
        mb: 1,
        backgroundColor: disabled ? "#f5f5f5" : "#fafcfc",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Box
        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}
      >
        <Box>
          <span style={labelStyle}>Type of Allergy</span>
          <Select
            options={ALLERGY_TYPE_OPTIONS}
            value={data.type}
            onChange={set("type")}
            styles={allergySelectStyles}
            menuPortalTarget={document.body}
            isSearchable={false}
          />
        </Box>
        <Box>
          <span style={labelStyle}>Agent</span>
          <Select
            options={ALLERGY_AGENT_OPTIONS}
            value={data.agent}
            onChange={set("agent")}
            placeholder="Select..."
            styles={allergySelectStyles}
            menuPortalTarget={document.body}
            isClearable
          />
        </Box>
      </Box>
      <Box sx={{ mb: 1 }}>
        <span style={labelStyle}>Reactions</span>
        <Select
          options={ALLERGY_REACTION_OPTIONS}
          value={data.reactions}
          onChange={set("reactions")}
          placeholder="Select..."
          styles={allergySelectStyles}
          menuPortalTarget={document.body}
          isClearable
        />
      </Box>
      <Box
        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}
      >
        <Box>
          <span style={labelStyle}>Start Date</span>
          <input
            type="date"
            style={fieldBase}
            value={data.startDate}
            onChange={(e) => set("startDate")(e.target.value)}
          />
        </Box>
        <Box>
          <span style={labelStyle}>Severity</span>
          <Select
            options={ALLERGY_SEVERITY_OPTIONS}
            value={data.severity}
            onChange={set("severity")}
            placeholder="Select..."
            styles={allergySelectStyles}
            menuPortalTarget={document.body}
            isClearable
          />
        </Box>
      </Box>
      <Box>
        <span style={labelStyle}>Comments</span>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="Add comments..."
          value={data.comments}
          onChange={(e) => set("comments")(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
              fontFamily: "Lato, sans-serif",
              borderRadius: "6px",
              backgroundColor: "white",
              "& fieldset": { borderColor: "#D0D8DA" },
              "&:hover fieldset": { borderColor: "#0097B2" },
              "&.Mui-focused fieldset": { borderColor: "#0097B2" },
            },
            "& .MuiOutlinedInput-input": {
              padding: "4px 6px",
              fontSize: "14px",
            },
          }}
        />
      </Box>
    </Box>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────
const PAST_MEDICAL_CONDITIONS = [
  "APS (Antiphospholid Antibody Syndrome)",
  "AVN Femur",
  "Abdominal Kochs",
  "Acid Peptic disease",
  "Anaemia",
  "Arthritis / CTD",
  "Carpal Tunnel Syndrome",
  "Central Nervous System",
  "Diabetes Mellitus",
  "Dyslipidemia",
  "Emphysema",
  "Epilepsy (seizures)",
  "Fibromyalgia",
  "Gastrointestinal Disorder",
  "Goiter",
  "Haematological Disorder",
  "History of Blood Transfusion",
  "History of TB",
  "Hypertension",
  "Hyperthyroidism",
  "Hypothyrodism",
  "IHD(Ischemic Heart Disease)",
  "Interstitial lung Disease (ILD)",
  "Kidney Ailment",
  "Liver Disease",
  "Lung Disorder",
  "Malignancy",
  "Migraine",
  "OA Knees",
  "Obstructive sleep Apnoea",
  "Oro -mandibular Dyskinesia",
  "Osteoporosis",
  "Psychiatric disturbances",
  "Pulmonary Arterial Hypertension",
  "Rheumatic Heart Disease",
  "Rheumatoid Arthritis",
  "Secondary Sjogrens syndrome",
  "Sjogrens Syndrome",
  "Skin Disease",
  "Systemic Lupus Erythematosus",
  "Systemic Sclerosis",
  "Takayasus Aortoarteritis",
  "Thyroid",
  "Ulcerative Colitis",
  "Upper Respiratory Tract Infection",
  "Urinary Tract Infection",
  "kidney disease",
];
const PAST_SURGICAL_CONDITIONS = [
  "Appendectomy",
  "Arterial Repair",
  "C-section",
  "CABG",
  "Cardiac Surgery",
  "Cataract Surgery",
  "Gall Bladder Removal",
  "Hernia Repair",
  "Hysterectomy",
  "Nephrectomy",
  "Prostate Surgery",
  "Tubal ligation",
  "Valve Replacement",
];
const PAST_DIAGNOSTIC_CONDITIONS = [
  "Abdominal U/S",
  "Arterial U/S",
  "Bladder U/S",
  "Bone Density",
  "Breast Bx",
  "Breast U/S",
  "Carotid U/S",
  "Chest Xray",
  "EKG",
  "Echocardiogram",
  "Kidney U/S",
  "MRI Cervical Spine",
  "MRI LS Spine",
  "Mammogram",
  "Optometry (Eye Exam)",
  "Papsmear",
  "Pelvic U/S",
  "Prostate U/S",
  "Sleep Study",
  "Spirometry",
  "Thyroid U/S",
  "Venous U/S",
];
const FAMILY_RELATION_OPTIONS = [
  "Aunt",
  "Brother",
  "Daughter",
  "Elder Brother",
  "Elder Sister",
  "Grand Father",
  "Grand Mother",
  "Maternal Aunt",
  "Maternal Cousin",
  "Maternal Grandfather",
  "Maternal Grandmother",
  "Maternal Uncle",
  "Paternal Aunt",
  "Paternal Cousin",
  "Paternal Grandfather",
  "Paternal Grandmother",
  "Paternal Uncle",
  "Sister",
  "Son",
  "Spouse",
  "Younger Brother",
  "Younger Sister",
];
const FAMILY_DISEASES = [
  "Arthritis / CTD",
  "Abdominal Kochs",
  "Acid Peptic disease",
  "Anaemia",
  "APS (Antiphospholid Antibody Syndrome)",
  "Bronchial Asthma",
  "Cancer",
  "Carpal Tunnel Syndrome",
  "Diabetes Mellitus",
  "Dyslipidemia",
  "Fibromyalgia",
  "Interstitial lung Disease (ILD)",
  "OA Knees",
  "Obstructive sleep Apnoea",
  "Oro -mandibular Dyskinesia",
  "Osteoporosis",
  "Pulmonary Arterial Hypertension",
  "Rheumatoid Arthritis",
  "Secondary Sjogrens syndrome",
  "Sjogrens Syndrome",
  "Systemic Lupus Erythematosus",
  "Systemic Sclerosis",
  "Skin Disease",
  "Takayasus Aortoarteritis",
  "Thyroid",
  "Upper Respiratory Tract Infection",
  "Urinary Tract Infection",
  "Hypertension",
  "IHD(Ischemic Heart Disease)",
  "TB Meningitis",
  "Psychiatric disturbances",
  "History of TB",
  "Malignancy",
];
const DRUG_OPTIONS = [
  "Marijuana",
  "Cocaine",
  "Heroin",
  "Methamphetamine",
  "Ecstasy",
  "LSD",
  "Opioids",
  "Other",
];
const TOBACCO_SMOKING_QTY = ["< 1 ppd", "1 ppd", "1-2 ppd", "> 2 ppd"];
const TOBACCO_CHEWING_QTY = ["1/day", "2-4/day", "5-10/day", "> 10/day"];
const familyStaticCols = [
  { key: "father", label: "Father" },
  { key: "mother", label: "Mother" },
];

const ConditionChip = ({ label, selected, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.2,
      py: 0.5,
      borderRadius: "20px",
      cursor: "pointer",
      border: selected ? "1.5px solid #0097B2" : "1.5px solid #D0D8DA",
      backgroundColor: selected ? "#E6F7FA" : "#FAFCFC",
      transition: "all 0.15s",
      "&:hover": { borderColor: "#0097B2", backgroundColor: "#F0FAFB" },
    }}
  >
    <Checkbox
      checked={selected}
      onChange={onToggle}
      size="small"
      onClick={(e) => e.stopPropagation()}
      sx={{
        p: 0,
        color: "#0097B2",
        "&.Mui-checked": { color: "#0097B2" },
        width: 14,
        height: 14,
      }}
    />
    <Typography
      sx={{
        fontSize: "14px",
        color: selected ? "#0097B2" : "#555",
        fontWeight: selected ? 600 : 400,
        userSelect: "none",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ProcedureTable = ({
  rows,
  updateRow,
  headers = ["Procedure", "Body Site", "Findings", "Date"],
  widths = ["24%", "21%", "24%", "30%"],
}) => (
  <TableContainer
    sx={{ border: "1px solid #E1E1E1", borderRadius: "8px", mb: 1 }}
  >
    <Table size="small" sx={{ tableLayout: "fixed" }}>
      <colgroup>
        {widths.map((w, i) => (
          <col key={i} style={{ width: w }} />
        ))}
      </colgroup>
      <TableHead>
        <TableRow sx={{ backgroundColor: "#F5F7F8" }}>
          {headers.map((h, i) => (
            <TableCell
              key={h}
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#666",
                textTransform: "uppercase",
                py: "7px",
                whiteSpace: "nowrap",
                borderRight: i < 3 ? "1px solid #E1E1E1" : "none",
              }}
            >
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            <TableCell sx={{ p: "4px", borderRight: "1px solid #E1E1E1" }}>
              <input
                style={{ ...fieldBase, fontSize: "14px", height: "28px" }}
                value={row.procedure}
                onChange={(e) => {
                  const v = e.target.value;
                  updateRow(i, "procedure", v);
                }}
              />
            </TableCell>
            <TableCell sx={{ p: "4px", borderRight: "1px solid #E1E1E1" }}>
              <input
                style={{ ...fieldBase, fontSize: "14px", height: "28px" }}
                value={row.body_site}
                onChange={(e) => {
                  const v = e.target.value;
                  updateRow(i, "body_site", v);
                }}
              />
            </TableCell>
            <TableCell sx={{ p: "4px", borderRight: "1px solid #E1E1E1" }}>
              <textarea
                style={{
                  ...fieldBase,
                  height: "28px",
                  resize: "none",
                  fontSize: "14px",
                  padding: "4px 6px",
                  lineHeight: "1.2",
                }}
                value={row.findings}
                onChange={(e) => {
                  const v = e.target.value;
                  updateRow(i, "findings", v);
                }}
              />
            </TableCell>
            <TableCell sx={{ p: "4px" }}>
              <input
                type="date"
                style={{ ...fieldBase, fontSize: "14px", height: "28px" }}
                value={row.date}
                onChange={(e) => {
                  const v = e.target.value;
                  updateRow(i, "date", v);
                }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const SaveBtn = ({ onClick }) => (
  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
    <Button
      variant="contained"
      sx={{ ...primaryBtnTeal, gap: 0.6, fontSize: "14px", p: 1.2 }}
      onClick={onClick}
    >
      <SaveIcon sx={{ width: 15, height: 15 }} /> Save
    </Button>
  </Box>
);

const TABS = [
  "Chief Complaints",
  "Allergies",
  "Past Medical History",
  "Past Surgical History",
  "Past Diagnostic History",
  "GYN History",
  "Past Pregnancies",
  "Family History",
  "Social History",
  "HPI",
  "ROS",
  "Impression Plan",
  "Instructions",
  "Referrals",
  "Vitals",
];
const TAB_KEYS = [
  "chief_complaints",
  "allergies",
  "past_medical_hx",
  "past_surgical_hx",
  "past_diagnostic_hx",
  "gyn_hx",
  "past_pregnancies_hx",
  "family_history",
  "social_hx",
  "hpi",
  "ros",
  "impression_plan",
  "instructions",
  "referrals",
  "vitals",
];

// ─── Main component ───────────────────────────────────────────────────────────
const ClinicalDataTabs = ({ patientDetailsData, appointmentData }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [tabMode, setTabMode] = useState("view"); // "view" | "add"
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(false);
  // console.log(appointmentData, "appointmentData");
  useEffect(() => {
    setTabMode("view");
    setViewData(null);
    if (!patientDetailsData?.id || !appointmentData?.id) return;
    const key = TAB_KEYS[activeTab];
    const keysToFetch =
      key === "hpi"
        ? ["hpi", "hpi_trans"]
        : key === "past_medical_hx"
          ? ["past_medical_hx", "past_medical_hx_trans"]
          : [key];
    setLoading(true);
    getClinicalData(
      patientDetailsData.id,
      appointmentData.id,
      keysToFetch,
    ).then((res) => {
      setLoading(false);
      const records = res?.payload?.clinical_data || [];
      if (key === "hpi") {
        const hpiRecord = records.find((r) => r.name === "hpi");
        const hpiTransRecord = records.find((r) => r.name === "hpi_trans");
        setViewData({
          hpi: hpiRecord?.value || null,
          hpi_trans: hpiTransRecord?.value || null,
        });
        return;
      }
      if (key === "past_medical_hx") {
        const main = records.find((r) => r.name === "past_medical_hx");
        const trans = records.find((r) => r.name === "past_medical_hx_trans");
        setViewData({ ...(main || {}), trans: trans?.value || null });
        return;
      }
      const record = records[0];
      setViewData(record || null);
      const d = record?.value;
      if (!d) return;
      if (key === "chief_complaints") {
        if (d.complaint)
          setChiefComplaintForm((p) => ({
            ...p,
            complaint: Array.isArray(d.complaint)
              ? d.complaint.map((c) => ({ label: c.trim(), value: c.trim() }))
              : d.complaint
                  .split(",")
                  .map((c) => ({ label: c.trim(), value: c.trim() }))
                  .filter((c) => c.value),
            duration: d.duration || "",
            comments: d.comments || "",
          }));
      } else if (key === "allergies") {
        setNkda(d.nkda || false);
        setAllergyNotes(typeof d.nkda === "string" ? d.nkda : "");
        if (d.items?.length)
          setAllergyRows(
            d.items
              .filter((e) => e.agent)
              .map((e) => ({
                type: e.type_of_allergy
                  ? { label: e.type_of_allergy, value: e.type_of_allergy }
                  : ALLERGY_TYPE_OPTIONS[0],
                agent: e.agent ? { label: e.agent, value: e.agent } : null,
                reactions: e.reactions
                  ? { label: e.reactions, value: e.reactions }
                  : null,
                severity: e.severity
                  ? { label: e.severity, value: e.severity }
                  : null,
                startDate: e.start_date || "",
                comments: e.comments || "",
              })),
          );
      } else if (key === "past_medical_hx") {
        setNoPastMedical(
          d.no_past_medical_hx === "1" ? "no_past_medical_history" : false,
        );
        setPastMedicalSelected(
          d.diseases
            ? d.diseases
                .split(",")
                .map((c) => c.trim())
                .filter(Boolean)
            : [],
        );
        setPastMedicalComments(d.comments || "");
      } else if (key === "surgical_history") {
        setNoPastSurgical(
          d.conditions === "no_past_surgical_history"
            ? "no_past_surgical_history"
            : false,
        );
        if (d.procedures?.length)
          setSurgicalRows((p) =>
            d.procedures
              .slice(0, 5)
              .concat(p.slice(d.procedures.slice(0, 5).length)),
          );
      } else if (key === "past_diagnostic_hx") {
        setNoPastDiagnostic(
          d.no_past_diagnostic_hx === "1"
            ? "no_past_diagnostic_history"
            : false,
        );
        if (d.items?.length)
          setDiagnosticRows((p) =>
            d.items
              .slice(0, 5)
              .map((e) => ({
                procedure: e.procedure || "",
                body_site: e.body_site || "",
                findings: e.findings || "",
                date: e.procedure_date || "",
              }))
              .concat(p.slice(d.items.slice(0, 5).length)),
          );
      } else if (key === "gyn_hx") {
        if (d.lmp) {
          setGynLmpRadio("lmp");
          setGynLmp(d.lmp);
        } else if (d.hysterectomy) {
          setGynLmpRadio("hysterectomy");
          setGynHysterectomy(d.hysterectomy);
        } else if (d.menopause) {
          setGynLmpRadio("menopause");
          setGynMenopause(d.menopause);
        }
        setGynAbnormalPap(d.abnormal_pap || "");
        setGynTotalPregnancies(d.total_pregnancies || "");
        setGynTreatment(d.treatment || "");
        if (d.menstrual_history) {
          setGynMenarche(d.menstrual_history.menarche || "");
          setGynCycle(d.menstrual_history.cycle || "");
          setGynCycleDuration(d.menstrual_history.cycle_duration || "");
          setGynFlow(d.menstrual_history.flow || "");
          setGynCramps(d.menstrual_history.cramps || "");
          setGynMenstrualProblems(d.menstrual_history.menstrual_problems || "");
        }
        if (d.birth_control) {
          const bc = d.birth_control;
          setGynBirthControl({
            none: bc.none || false,
            pills: !!bc.pills,
            condoms: bc.condoms || false,
            btl: !!bc.btl,
            contraceptiveRing: bc.contraceptive_ring || false,
            vasectomy: !!bc.vasectomy,
            implanon: !!bc.implanon,
            attemptingPregnancy: bc.attempting_pregnancy || false,
            iud: !!bc.iud,
            others: !!bc.others,
          });
          if (bc.pills && typeof bc.pills === "string")
            setGynPillsText(bc.pills);
          if (bc.btl && typeof bc.btl === "string") setGynBtlText(bc.btl);
          if (bc.vasectomy && typeof bc.vasectomy === "string")
            setGynVasectomyText(bc.vasectomy);
          if (bc.implanon && typeof bc.implanon === "string")
            setGynImplanonText(bc.implanon);
          if (bc.iud && typeof bc.iud === "string") setGynIudType(bc.iud);
          setGynIudInsertDate(bc["Insert Date"] || "");
          setGynIudRemovalDate(bc["Removal Date"] || "");
          if (bc.others && typeof bc.others === "string")
            setGynOthersText(bc.others);
        }
      } else if (key === "past_pregnancies_hx") {
        if (d.items?.length)
          setPregnancyRows((p) =>
            d.items.slice(0, 10).concat(p.slice(d.items.slice(0, 10).length)),
          );
      } else if (key === "family_history") {
        setNoFamilyIllness(d.no_family_illness || false);
        if (d.father)
          setFamilyStaticData((p) => ({
            ...p,
            father: {
              age: d.father.age || "",
              livingStatus: d.father.living_status || "",
              checked: Object.fromEntries(
                (d.father.diseases || []).map((x) => [x, true]),
              ),
              extraInput: "",
            },
          }));
        if (d.mother)
          setFamilyStaticData((p) => ({
            ...p,
            mother: {
              age: d.mother.age || "",
              livingStatus: d.mother.living_status || "",
              checked: Object.fromEntries(
                (d.mother.diseases || []).map((x) => [x, true]),
              ),
              extraInput: "",
            },
          }));
        if (d.members?.length) {
          setFamilyDefaultSelectCols((p) =>
            d.members
              .slice(0, 2)
              .map((m, i) => ({
                relation: m.relation || "",
                age: m.age || "",
                livingStatus: m.living_status || "",
                checked: Object.fromEntries(
                  (m.diseases || []).map((x) => [x, true]),
                ),
                extraInput: p[i]?.extraInput || "",
                extraInputs: m.malignancy ? { Malignancy: m.malignancy } : {},
              }))
              .concat(p.slice(d.members.slice(0, 2).length)),
          );
        }
        setFamilyComments(d.comments || "");
      } else if (key === "social_hx") {
        setSocialMaritalStatus(d.maritalstatus || "");
        setSocialOccupation(d.occupation || "");
        setSocialIndustry(d.industry || "");
        setSocialStress(d.generalstresslevel || "");
        setSocialDiet(d.diet || "");
        setSocialCaffeineIntake(d.caffeineintake || "");
        setSocialTobaccoSmoking(d.tobacco_status || "");
        setSocialTobaccoSmokingQty(d.tobacco_quan || "");
        setSocialTobaccoSmokingDuration(d.tobacco_quit_duration || "");
        setSocialTobaccoChewing(d.chewing_status || "");
        setSocialTobaccoChewingQty(d.chewing_quan || "");
        setSocialTobaccoChewingDuration(d.chewing_quit_duration || "");
        setSocialAlcohol(d.alcohol || "");
        setSocialAlcoholIfYes(d.alcohol_yes || "");
        setSocialAlcoholDuration(d.alcohol_quit_duration || "");
        setSocialDrugs(d.drugs || "");
        setSocialDrugsSelected(
          d.drugs_yes
            ? d.drugs_yes
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        );
        setSocialDrugsDuration(d.drugs_quit_duration || "");
        setSocialExercise(d.excercise || "");
        setSocialComments(d.comments || "");
      }
    });
  }, [activeTab, patientDetailsData?.id, appointmentData?.id]);

  // Pre-fill vitals form from appointmentData when vitals tab is active
  useEffect(() => {
    if (TAB_KEYS[activeTab] !== "vitals" || !appointmentData) return;
    setVitalsBpS(appointmentData.bp_s ?? "");
    setVitalsBpD(appointmentData.bp_d ?? "");
    setVitalsHeight(appointmentData.height ?? "");
    setVitalsWeight(appointmentData.weight ?? "");
    setVitalsFever(appointmentData.fever ?? "");
    setVitalsBmi(appointmentData.bmi ?? "");
    setVitalsPulseRate(appointmentData.pulse_rate ?? "");
    setVitalsHeartRate(appointmentData.heart_rate ?? "");
    setVitalsPainLevel(appointmentData.pain_level ?? "");
    setVitalsSpo2(appointmentData.spo2 ?? "");
    setVitalsAlcohol(appointmentData.alcohol || false);
    setVitalsTobacco(appointmentData.tobacco || false);
    setVitalsSmoking(appointmentData.smoking || false);
    setVitalsComments(appointmentData.comments || "");
  }, [activeTab, appointmentData]);

  // ── Chief Complaint ──
  const [chiefComplaintForm, setChiefComplaintForm] = useState({
    complaint: null,
    duration: "",
    comments: "",
  });
  const { data: chiefComplaintsData } = useQuery({
    queryKey: ["staticDataList", "chief_complaints"],
    queryFn: () => getStaticDataList("chief_complaints"),
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
  const complaintOptions = (
    chiefComplaintsData?.payload?.data?.complaints || []
  ).map((c) => ({ label: c, value: c }));

  // ── Allergies ──
  const [nkda, setNkda] = useState(false);
  const [allergyNotes, setAllergyNotes] = useState("");
  const [allergyExtraRows, setAllergyExtraRows] = useState(0);
  const [allergyRows, setAllergyRows] = useState([
    emptyAllergyRow(),
    emptyAllergyRow(),
    emptyAllergyRow(),
  ]);
  const updateAllergyRow = (i, data) =>
    setAllergyRows((p) => p.map((r, idx) => (idx === i ? data : r)));

  // ── Past Medical ──
  const [noPastMedical, setNoPastMedical] = useState(false);
  const [pastMedicalSelected, setPastMedicalSelected] = useState([]);
  const [pastMedicalComments, setPastMedicalComments] = useState("");
  const togglePastMedical = (c) =>
    setPastMedicalSelected((p) =>
      p.includes(c) ? p.filter((x) => x !== c) : [...p, c],
    );

  // ── Past Surgical ──
  const [noPastSurgical, setNoPastSurgical] = useState(false);
  const [showMoreSurgeries, setShowMoreSurgeries] = useState(false);
  const emptySurgicalRow = () => ({
    procedure: "",
    body_site: "",
    findings: "",
    date: "",
  });
  const [surgicalRows, setSurgicalRows] = useState(
    Array.from({ length: 5 }, emptySurgicalRow),
  );
  const [surgicalMoreRows, setSurgicalMoreRows] = useState(
    Array.from({ length: 10 }, emptySurgicalRow),
  );
  const updateSurgicalRow = (i, field, val) =>
    setSurgicalRows((p) =>
      p.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)),
    );
  const updateSurgicalMoreRow = (i, field, val) =>
    setSurgicalMoreRows((p) =>
      p.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)),
    );
  const addSurgicalProcedureFromChip = (procedure) => {
    setSurgicalRows((prev) => {
      const existingIdx = prev.findIndex(
        (r) => r.fromChip && r.procedure === procedure,
      );
      if (existingIdx !== -1)
        return prev.map((r, i) =>
          i === existingIdx ? { ...r, procedure: "", fromChip: false } : r,
        );
      const emptyIdx = prev.findIndex((r) => !r.procedure);
      if (emptyIdx !== -1)
        return prev.map((r, i) =>
          i === emptyIdx ? { ...r, procedure, fromChip: true } : r,
        );
      return [
        ...prev,
        { procedure, body_site: "", findings: "", date: "", fromChip: true },
      ];
    });
  };

  // ── Past Diagnostic ──
  const [noPastDiagnostic, setNoPastDiagnostic] = useState(false);
  const [showMoreDiagnostic, setShowMoreDiagnostic] = useState(false);
  const emptyDiagnosticRow = () => ({
    procedure: "",
    body_site: "",
    findings: "",
    date: "",
    fromChip: false,
  });
  const [diagnosticRows, setDiagnosticRows] = useState(
    Array.from({ length: 5 }, emptyDiagnosticRow),
  );
  const [diagnosticMoreRows, setDiagnosticMoreRows] = useState(
    Array.from({ length: 10 }, emptyDiagnosticRow),
  );
  const updateDiagnosticRow = (i, field, val) =>
    setDiagnosticRows((p) =>
      p.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)),
    );
  const updateDiagnosticMoreRow = (i, field, val) =>
    setDiagnosticMoreRows((p) =>
      p.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)),
    );
  const addDiagnosticProcedureFromChip = (procedure) => {
    setDiagnosticRows((prev) => {
      const existingIdx = prev.findIndex(
        (r) => r.fromChip && r.procedure === procedure,
      );
      if (existingIdx !== -1)
        return prev.map((r, i) =>
          i === existingIdx ? { ...r, procedure: "", fromChip: false } : r,
        );
      const emptyIdx = prev.findIndex((r) => !r.procedure);
      if (emptyIdx !== -1)
        return prev.map((r, i) =>
          i === emptyIdx ? { ...r, procedure, fromChip: true } : r,
        );
      return [
        ...prev,
        { procedure, body_site: "", findings: "", date: "", fromChip: true },
      ];
    });
  };

  // ── Gyn History ──
  const [gynLmpRadio, setGynLmpRadio] = useState("lmp");
  const [gynLmp, setGynLmp] = useState("");
  const [gynHysterectomy, setGynHysterectomy] = useState("");
  const [gynMenopause, setGynMenopause] = useState("");
  const [gynAbnormalPap, setGynAbnormalPap] = useState("");
  const [gynTotalPregnancies, setGynTotalPregnancies] = useState("");
  const [gynTreatment, setGynTreatment] = useState("");
  const [gynMenarche, setGynMenarche] = useState("");
  const [gynCycle, setGynCycle] = useState("");
  const [gynCycleDuration, setGynCycleDuration] = useState("");
  const [gynFlow, setGynFlow] = useState("");
  const [gynCramps, setGynCramps] = useState("");
  const [gynMenstrualProblems, setGynMenstrualProblems] = useState("");
  const [gynBirthControl, setGynBirthControl] = useState({
    none: false,
    pills: false,
    condoms: false,
    btl: false,
    contraceptiveRing: false,
    vasectomy: false,
    implanon: false,
    attemptingPregnancy: false,
    iud: false,
    others: false,
  });
  const [gynPillsText, setGynPillsText] = useState("");
  const [gynBtlText, setGynBtlText] = useState("");
  const [gynVasectomyText, setGynVasectomyText] = useState("");
  const [gynImplanonText, setGynImplanonText] = useState("");
  const [gynIudType, setGynIudType] = useState("");
  const [gynIudInsertDate, setGynIudInsertDate] = useState("");
  const [gynIudRemovalDate, setGynIudRemovalDate] = useState("");
  const [gynOthersText, setGynOthersText] = useState("");

  // ── Past Pregnancies ──
  const [pregnancyMoreRows, setPregnancyMoreRows] = useState(0);
  const emptyPregnancyRow = () => ({
    date: "",
    ga_weeks: "",
    birth_weight: "",
    type_of_delivery: "",
    place_of_delivery: "",
    preterm_labor: "",
    outcome: "",
    comments: "",
  });
  const [pregnancyRows, setPregnancyRows] = useState(
    Array.from({ length: 10 }, emptyPregnancyRow),
  );
  const updatePregnancyRow = (i, field, val) =>
    setPregnancyRows((p) =>
      p.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)),
    );

  // ── Family History ──
  const [noFamilyIllness, setNoFamilyIllness] = useState(false);
  const [familyStaticData, setFamilyStaticData] = useState({
    father: { age: "", livingStatus: "", checked: {}, extraInput: "" },
    mother: { age: "", livingStatus: "", checked: {}, extraInput: "" },
  });
  const [familyDefaultSelectCols, setFamilyDefaultSelectCols] = useState([
    { relation: "", age: "", livingStatus: "", checked: {}, extraInput: "" },
    { relation: "", age: "", livingStatus: "", checked: {}, extraInput: "" },
  ]);
  const [familyExtraCols, setFamilyExtraCols] = useState([]);
  const [familyComments, setFamilyComments] = useState("");
  const updateStaticCol = (key, field, value) =>
    setFamilyStaticData((p) => ({
      ...p,
      [key]: { ...p[key], [field]: value },
    }));
  const toggleStaticDisease = (key, disease) =>
    setFamilyStaticData((p) => ({
      ...p,
      [key]: {
        ...p[key],
        checked: { ...p[key].checked, [disease]: !p[key].checked[disease] },
      },
    }));
  const updateDefaultSelectCol = (i, field, value) =>
    setFamilyDefaultSelectCols((p) =>
      p.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)),
    );
  const toggleDefaultSelectDisease = (i, disease) =>
    setFamilyDefaultSelectCols((p) =>
      p.map((c, idx) =>
        idx === i
          ? { ...c, checked: { ...c.checked, [disease]: !c.checked[disease] } }
          : c,
      ),
    );
  const addFamilyCol = () => {
    if (familyExtraCols.length >= 2) return;
    setFamilyExtraCols((p) => [
      ...p,
      { relation: "", age: "", livingStatus: "", checked: {}, extraInputs: {} },
    ]);
  };
  const removeFamilyCol = () => setFamilyExtraCols((p) => p.slice(0, -1));
  const updateExtraCol = (i, field, value) =>
    setFamilyExtraCols((p) =>
      p.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)),
    );
  const toggleExtraDisease = (i, disease) =>
    setFamilyExtraCols((p) =>
      p.map((c, idx) =>
        idx === i
          ? { ...c, checked: { ...c.checked, [disease]: !c.checked[disease] } }
          : c,
      ),
    );
  const updateExtraColDiseaseInput = (i, disease, val) =>
    setFamilyExtraCols((p) =>
      p.map((c, idx) =>
        idx === i
          ? { ...c, extraInputs: { ...c.extraInputs, [disease]: val } }
          : c,
      ),
    );

  // ── Social History ──
  const [socialMaritalStatus, setSocialMaritalStatus] = useState("");
  const [socialEducation, setSocialEducation] = useState("");
  const [socialOccupation, setSocialOccupation] = useState("");
  const [socialIndustry, setSocialIndustry] = useState("");
  const [socialStress, setSocialStress] = useState("");
  const [socialDiet, setSocialDiet] = useState("");
  const [socialCaffeineIntake, setSocialCaffeineIntake] = useState("");
  const [socialTobaccoSmoking, setSocialTobaccoSmoking] = useState("");
  const [socialTobaccoSmokingQty, setSocialTobaccoSmokingQty] = useState("");
  const [socialTobaccoSmokingDuration, setSocialTobaccoSmokingDuration] =
    useState("");
  const [socialTobaccoChewing, setSocialTobaccoChewing] = useState("");
  const [socialTobaccoChewingQty, setSocialTobaccoChewingQty] = useState("");
  const [socialTobaccoChewingDuration, setSocialTobaccoChewingDuration] =
    useState("");
  const [socialAlcohol, setSocialAlcohol] = useState("");
  const [socialAlcoholIfYes, setSocialAlcoholIfYes] = useState("");
  const [socialAlcoholDuration, setSocialAlcoholDuration] = useState("");
  const [socialDrugs, setSocialDrugs] = useState("");
  const [socialDrugsSelected, setSocialDrugsSelected] = useState([]);
  const [socialDrugsDuration, setSocialDrugsDuration] = useState("");
  const [socialExercise, setSocialExercise] = useState("");
  const [socialComments, setSocialComments] = useState("");

  // ── API helper ──
  const callBatchUpsert = async (formKey, formData) => {
    if (!patientDetailsData?.id || !appointmentData?.id) {
      showToast("Please select a patient and appointment first.", "ERROR");
      return;
    }
    const result = await batchUpsertClinicalData(
      patientDetailsData.id,
      appointmentData.id,
      { forms_to_be_updated: [formKey], [formKey]: formData },
    );
    showToast(
      result?.status?.message || result?.status?.status || "Saved",
      result?.status?.status || "SUCCESS",
    );
  };

  // ── Save handlers ──
  const saveChiefComplaints = () =>
    callBatchUpsert("chief_complaints", {
      complaint: Array.isArray(chiefComplaintForm.complaint)
        ? chiefComplaintForm.complaint.map((c) => c.value).join(", ")
        : chiefComplaintForm.complaint?.value || "",
      duration: chiefComplaintForm.duration,
      comments: chiefComplaintForm.comments,
    });

  const saveAllergies = () =>
    callBatchUpsert("allergies", {
      nkda: nkda ? allergyNotes : false,
      ...(nkda
        ? {}
        : {
            items: allergyRows
              .filter((r) => r.agent || r.type)
              .map((r) => ({
                type_of_allergy: r.type?.value || "",
                agent: r.agent?.value || "",
                start_date: r.startDate || "",
                reactions: r.reactions?.value || "",
                severity: r.severity?.value || "",
                comments: r.comments || "",
              })),
          }),
    });

  const savePastMedical = () => {
    const isNo = noPastMedical === "no_past_medical_history";
    callBatchUpsert("past_medical_hx", {
      no_past_medical_hx: isNo ? "1" : "0",
      diseases: isNo ? "" : pastMedicalSelected.join(", "),
      comments: pastMedicalComments,
    });
  };

  const savePastSurgical = () => {
    const isNo = noPastSurgical === "no_past_surgical_history";
    const allRows = [
      ...surgicalRows,
      ...(showMoreSurgeries ? surgicalMoreRows : []),
    ].filter((r) => r.procedure || r.body_site || r.findings || r.date);
    callBatchUpsert("past_surgical_hx", {
      no_past_surgical_hx: isNo ? "1" : "0",

      items: isNo
        ? []
        : allRows.map((r) => ({
            procedure: r.procedure,
            body_site: r.body_site,
            findings: r.findings,
            procedure_date: r.date,
            no_past_surgical_hx: isNo ? "1" : "0.0",
          })),
    });
  };

  const savePastDiagnostic = () => {
    const isNo = noPastDiagnostic === "no_past_diagnostic_history";
    const allRows = [
      ...diagnosticRows,
      ...(showMoreDiagnostic ? diagnosticMoreRows : []),
    ].filter((r) => r.procedure || r.body_site || r.findings || r.date);
    callBatchUpsert("past_diagnostic_hx", {
      no_past_diagnostic_hx: isNo ? "1" : "0",
      items: isNo
        ? []
        : allRows.map((r) => ({
            procedure: r.procedure,
            body_site: r.body_site,
            findings: r.findings,
            procedure_date: r.date,
            npdh: "0",
          })),
    });
  };

  const saveGynHistory = () => {
    const lmpField =
      gynLmpRadio === "lmp"
        ? { lmp: gynLmp }
        : gynLmpRadio === "hysterectomy"
          ? { hysterectomy: gynHysterectomy }
          : { menopause: gynMenopause };

    const birthControl =
      gynLmpRadio === "lmp"
        ? {
            none: gynBirthControl.none,
            pills: gynBirthControl.pills ? gynPillsText || true : false,
            btl: gynBirthControl.btl ? gynBtlText || true : false,
            vasectomy: gynBirthControl.vasectomy
              ? gynVasectomyText || true
              : false,
            condoms: gynBirthControl.condoms,
            contraceptive_ring: gynBirthControl.contraceptiveRing,
            attempting_pregnancy: gynBirthControl.attemptingPregnancy,
            implanon: gynBirthControl.implanon
              ? gynImplanonText || true
              : false,
            iud: gynBirthControl.iud ? gynIudType || true : false,
            "Insert Date": gynIudInsertDate || "",
            "Removal Date": gynIudRemovalDate || "",
            others: gynBirthControl.others ? gynOthersText || true : false,
          }
        : {};

    callBatchUpsert("gyn_hx", {
      ...lmpField,
      abnormal_pap: gynAbnormalPap,
      total_pregnancies: gynTotalPregnancies,
      treatment: gynTreatment,
      menstrual_history: {
        menarche: gynMenarche,
        cycle: gynCycle,
        cycle_duration: gynCycleDuration,
        flow: gynFlow,
        cramps: gynCramps,
        menstrual_problems: gynMenstrualProblems,
      },
      birth_control: birthControl,
    });
  };

  const savePastPregnancies = () => {
    const allRows = pregnancyRows.filter(
      (r) =>
        r.date ||
        r.ga_weeks ||
        r.birth_weight ||
        r.type_of_delivery ||
        r.place_of_delivery ||
        r.preterm_labor ||
        r.outcome ||
        r.comments,
    );
    callBatchUpsert("past_pregnancies_hx", { items: allRows });
  };

  const saveFamilyHistory = () => {
    const formatStaticMember = (data) => ({
      age: data.age,
      living_status: data.livingStatus,
      diseases: Object.entries(data.checked)
        .filter(([, v]) => v)
        .map(([k]) => k),
    });
    const formatSelectMember = (col) => ({
      relation: col.relation,
      age: col.age,
      living_status: col.livingStatus,
      diseases: Object.entries(col.checked)
        .filter(([, v]) => v)
        .map(([k]) => k),
      malignancy: col.extraInputs?.["Malignancy"] || "",
    });
    if (noFamilyIllness) {
      callBatchUpsert("family_history", {
        no_family_illness: true,
        comments: familyComments,
      });
    } else {
      callBatchUpsert("family_history", {
        no_family_illness: false,
        father: formatStaticMember(familyStaticData.father),
        mother: formatStaticMember(familyStaticData.mother),
        members: familyDefaultSelectCols
          .concat(familyExtraCols)
          .filter((c) => c.relation)
          .map(formatSelectMember),
        comments: familyComments,
      });
    }
  };

  const saveSocialHistory = () =>
    callBatchUpsert("social_hx", {
      maritalstatus: socialMaritalStatus,
      occupation: socialOccupation,
      industry: socialIndustry,
      generalstresslevel: socialStress,
      diet: socialDiet,
      caffeineintake: socialCaffeineIntake,
      tobacco_status: socialTobaccoSmoking,
      tobacco_quan: socialTobaccoSmokingQty,
      tobacco_quit_duration: socialTobaccoSmokingDuration,
      chewing_status: socialTobaccoChewing,
      chewing_quan: socialTobaccoChewingQty,
      chewing_quit_duration: socialTobaccoChewingDuration,
      alcohol: socialAlcohol,
      alcohol_yes: socialAlcoholIfYes,
      alcohol_quit_duration: socialAlcoholDuration,
      drugs: socialDrugs,
      drugs_yes: socialDrugsSelected.join(", "),
      drugs_quit_duration: socialDrugsDuration,
      excercise: socialExercise,
      comments: socialComments,
    });

  // ─── Tab content renderers ─────────────────────────────────────────────────
  const renderChiefComplaints = () => (
    <Box sx={{ p: 2 }}>
      <Select
        placeholder="Select complaint..."
        options={complaintOptions}
        value={chiefComplaintForm.complaint}
        onChange={(val) =>
          setChiefComplaintForm((p) => ({ ...p, complaint: val }))
        }
        styles={selectStyles}
        menuPortalTarget={document.body}
        isMulti
        isClearable
      />
      <Box sx={{ mt: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Duration (e.g. 3 days)"
          variant="outlined"
          sx={textfieldTextPrep}
          value={chiefComplaintForm.duration}
          onChange={(e) =>
            setChiefComplaintForm((p) => ({ ...p, duration: e.target.value }))
          }
        />
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Additional comments..."
          variant="outlined"
          multiline
          minRows={3}
          sx={{
            ...textfieldTextPrep,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "white",
            },
          }}
          value={chiefComplaintForm.comments}
          onChange={(e) =>
            setChiefComplaintForm((p) => ({ ...p, comments: e.target.value }))
          }
        />
      </Box>
      <SaveBtn onClick={saveChiefComplaints} />
    </Box>
  );

  const renderAllergies = () => (
    <Box sx={{ p: 1.5 }}>
      <Box
        sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", mt: 0.8, flexShrink: 0 }}
        >
          <Checkbox
            checked={nkda}
            onChange={(e) => setNkda(e.target.checked)}
            sx={{
              p: 0.5,
              color: "#0097B2",
              "&.Mui-checked": { color: "#0097B2" },
            }}
          />
          <Typography variant="body2" fontWeight={500}>
            NKDA
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            disabled={!nkda}
            value={allergyNotes}
            onChange={(e) => {
              if (e.target.value.length <= 300) setAllergyNotes(e.target.value);
            }}
            inputProps={{ maxLength: 300 }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: nkda ? "white" : "#f0f0f0",
                fontSize: "14px",
              },
            }}
          />
          <Typography variant="caption" sx={{ color: "#0097B2", ml: 0.5 }}>
            {300 - allergyNotes.length}
          </Typography>
        </Box>
      </Box>
      {!nkda && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 1.5,
            gap: 1,
          }}
        >
          <select
            value={allergyExtraRows}
            onChange={(e) => setAllergyExtraRows(Number(e.target.value))}
            style={{
              border: "1px solid #B0BEC5",
              borderRadius: "6px",
              fontSize: "14px",
              padding: "3px 8px",
              backgroundColor: "white",
              color: "#333",
              cursor: "pointer",
              outline: "none",
              fontFamily: "Lato, sans-serif",
              minWidth: "70px",
            }}
          >
            <option value={0}>More</option>
            {Array.from({ length: 15 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </Box>
      )}
      {allergyRows.map((row, idx) => (
        <AllergyRow
          key={idx}
          data={row}
          onChange={(updated) => updateAllergyRow(idx, updated)}
          disabled={nkda}
        />
      ))}
      {!nkda && allergyExtraRows > 0 && (
        <Box sx={{ mt: 1, pt: 1, borderTop: "2px dashed #E1E1E1" }}>
          {Array.from({ length: allergyExtraRows }, (_, i) => (
            <AllergyRow
              key={`extra-${i}`}
              data={allergyRows[3 + i] || emptyAllergyRow()}
              onChange={(updated) => updateAllergyRow(3 + i, updated)}
              disabled={false}
            />
          ))}
        </Box>
      )}
      <SaveBtn onClick={saveAllergies} />
    </Box>
  );

  const renderPastMedical = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Checkbox
          checked={noPastMedical === "no_past_medical_history"}
          onChange={(e) =>
            setNoPastMedical(
              e.target.checked ? "no_past_medical_history" : false,
            )
          }
          size="small"
          sx={{ p: 0, color: "#0097B2", "&.Mui-checked": { color: "#0097B2" } }}
        />
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>
          No Past Medical History
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          opacity: noPastMedical ? 0.4 : 1,
          pointerEvents: noPastMedical ? "none" : "auto",
          mb: 2,
        }}
      >
        {PAST_MEDICAL_CONDITIONS.map((c) => (
          <ConditionChip
            key={c}
            label={c}
            selected={pastMedicalSelected.includes(c)}
            onToggle={() => togglePastMedical(c)}
          />
        ))}
      </Box>
      <Box sx={{ mb: 2 }}>
        <label style={labelStyle}>Comments</label>
        <textarea
          value={pastMedicalComments}
          onChange={(e) => setPastMedicalComments(e.target.value)}
          rows={3}
          style={{
            ...fieldBase,
            height: "auto",
            resize: "vertical",
            padding: "6px",
          }}
          placeholder="Add any additional comments..."
        />
      </Box>
      <SaveBtn onClick={savePastMedical} />
    </Box>
  );

  const renderPastSurgical = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Checkbox
          checked={noPastSurgical === "no_past_surgical_history"}
          onChange={(e) => {
            setNoPastSurgical(
              e.target.checked ? "no_past_surgical_history" : false,
            );
          }}
          size="small"
          sx={{ p: 0, color: "#0097B2", "&.Mui-checked": { color: "#0097B2" } }}
        />
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>
          No Past Surgical History
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          mb: 2,
          opacity: noPastSurgical === "no_past_surgical_history" ? 0.4 : 1,
          pointerEvents:
            noPastSurgical === "no_past_surgical_history" ? "none" : "auto",
        }}
      >
        {PAST_SURGICAL_CONDITIONS.map((c) => {
          const isSelected = surgicalRows.some(
            (r) => r.fromChip && r.procedure === c,
          );
          return (
            <Box
              key={c}
              onClick={() => addSurgicalProcedureFromChip(c)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.2,
                py: 0.5,
                borderRadius: "20px",
                cursor: "pointer",
                border: isSelected
                  ? "1.5px solid #0097B2"
                  : "1.5px solid #D0D8DA",
                backgroundColor: isSelected ? "#E6F7FA" : "#FAFCFC",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: "#0097B2",
                  backgroundColor: "#F0FAFB",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: isSelected ? "#0097B2" : "#555",
                  fontWeight: isSelected ? 600 : 400,
                  userSelect: "none",
                }}
              >
                {c}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          opacity: noPastSurgical === "no_past_surgical_history" ? 0.4 : 1,
          pointerEvents:
            noPastSurgical === "no_past_surgical_history" ? "none" : "auto",
        }}
      >
        <ProcedureTable rows={surgicalRows} updateRow={updateSurgicalRow} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Box
            onClick={() => setShowMoreSurgeries((p) => !p)}
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#0097B2",
              cursor: "pointer",
              textDecoration: "underline",
              userSelect: "none",
            }}
          >
            {showMoreSurgeries ? "Less" : "More"}
          </Box>
        </Box>
        {showMoreSurgeries && (
          <ProcedureTable
            rows={surgicalMoreRows}
            updateRow={updateSurgicalMoreRow}
          />
        )}
      </Box>
      <SaveBtn onClick={savePastSurgical} />
    </Box>
  );

  const renderPastDiagnostic = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Checkbox
          checked={noPastDiagnostic === "no_past_diagnostic_history"}
          onChange={(e) => {
            setNoPastDiagnostic(
              e.target.checked ? "no_past_diagnostic_history" : false,
            );
          }}
          size="small"
          sx={{ p: 0, color: "#0097B2", "&.Mui-checked": { color: "#0097B2" } }}
        />
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>
          No Past Diagnostic History
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          mb: 2,
          opacity: noPastDiagnostic === "no_past_diagnostic_history" ? 0.4 : 1,
          pointerEvents:
            noPastDiagnostic === "no_past_diagnostic_history" ? "none" : "auto",
        }}
      >
        {PAST_DIAGNOSTIC_CONDITIONS.map((c) => {
          const isSelected = diagnosticRows.some(
            (r) => r.fromChip && r.procedure === c,
          );
          return (
            <Box
              key={c}
              onClick={() => addDiagnosticProcedureFromChip(c)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.2,
                py: 0.5,
                borderRadius: "20px",
                cursor: "pointer",
                border: isSelected
                  ? "1.5px solid #0097B2"
                  : "1.5px solid #D0D8DA",
                backgroundColor: isSelected ? "#E6F7FA" : "#FAFCFC",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: "#0097B2",
                  backgroundColor: "#F0FAFB",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: isSelected ? "#0097B2" : "#555",
                  fontWeight: isSelected ? 600 : 400,
                  userSelect: "none",
                }}
              >
                {c}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          opacity: noPastDiagnostic === "no_past_diagnostic_history" ? 0.4 : 1,
          pointerEvents:
            noPastDiagnostic === "no_past_diagnostic_history" ? "none" : "auto",
        }}
      >
        <ProcedureTable rows={diagnosticRows} updateRow={updateDiagnosticRow} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Box
            onClick={() => setShowMoreDiagnostic((p) => !p)}
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#0097B2",
              cursor: "pointer",
              textDecoration: "underline",
              userSelect: "none",
            }}
          >
            {showMoreDiagnostic ? "Less" : "More"}
          </Box>
        </Box>
        {showMoreDiagnostic && (
          <ProcedureTable
            rows={diagnosticMoreRows}
            updateRow={updateDiagnosticMoreRow}
          />
        )}
      </Box>
      <SaveBtn onClick={savePastDiagnostic} />
    </Box>
  );

  const renderGynHistory = () => {
    const isBirthControlActive = gynLmpRadio === "lmp";

    return (
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 3.5 }}>
        {/* Reproductive Status (LMP / Hysterectomy / Menopause) */}
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#006C7F",
              mb: 2,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span
              style={{
                width: "4px",
                height: "16px",
                backgroundColor: "#0097B2",
                borderRadius: "2px",
                display: "inline-block",
              }}
            ></span>
            Reproductive Status
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            {[
              { value: "lmp", label: "LMP" },
              { value: "hysterectomy", label: "Hysterectomy" },
              { value: "menopause", label: "Menopause" },
            ].map(({ value, label }) => {
              const isSelected = gynLmpRadio === value;
              return (
                <Box
                  key={value}
                  onClick={() => setGynLmpRadio(value)}
                  sx={{
                    border: isSelected
                      ? "1.5px solid #0097B2"
                      : "1.5px solid #E1E8EA",
                    borderRadius: "10px",
                    p: 2,
                    backgroundColor: isSelected ? "#F4FBFB" : "white",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    boxShadow: isSelected
                      ? "0 4px 12px rgba(0, 151, 178, 0.06)"
                      : "none",
                    "&:hover": {
                      borderColor: "#0097B2",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <input
                      type="radio"
                      name="gynLmpRadioPrx"
                      value={value}
                      checked={isSelected}
                      onChange={() => {}} // Box onClick handles this
                      style={{
                        accentColor: "#0097B2",
                        width: 15,
                        height: 15,
                        cursor: "pointer",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: isSelected ? "#006C7F" : "#4A5568",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <input
                    placeholder={
                      value === "lmp"
                        ? "Select date"
                        : value === "hysterectomy"
                          ? "Enter date"
                          : "Enter menopause details"
                    }
                    style={{
                      ...fieldBase,
                      fontSize: "14px",
                      height: "32px",
                      borderRadius: "6px",
                      padding: "0 8px",
                      backgroundColor: isSelected ? "white" : "#F7F9FA",
                      borderColor: isSelected ? "#0097B2" : "#D0D8DA",
                      color: isSelected ? "#333" : "#aaa",
                      pointerEvents: isSelected ? "auto" : "none",
                    }}
                    disabled={!isSelected}
                    value={
                      value === "lmp"
                        ? gynLmp
                        : value === "hysterectomy"
                          ? gynHysterectomy
                          : gynMenopause
                    }
                    onClick={(e) => e.stopPropagation()} // Prevent radio select toggle on input click
                    onChange={(e) => {
                      if (value === "lmp") setGynLmp(e.target.value);
                      else if (value === "hysterectomy")
                        setGynHysterectomy(e.target.value);
                      else setGynMenopause(e.target.value);
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Clinical History Info */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#4A5568" }}
            >
              Abnormal Pap
            </Typography>
            <select
              value={gynAbnormalPap}
              onChange={(e) => setGynAbnormalPap(e.target.value)}
              style={{
                ...fieldBase,
                fontSize: "14px",
                height: "36px",
                borderRadius: "8px",
                padding: "0 10px",
                cursor: "pointer",
              }}
            >
              <option value="">Select option...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#4A5568" }}
            >
              Total Pregnancies
            </Typography>
            <input
              style={{
                ...fieldBase,
                fontSize: "14px",
                height: "36px",
                borderRadius: "8px",
                padding: "0 10px",
              }}
              placeholder="e.g. 2"
              value={gynTotalPregnancies}
              onChange={(e) => setGynTotalPregnancies(e.target.value)}
            />
          </Box>
        </Box>

        {/* Treatment Details */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 600, color: "#4A5568" }}
          >
            Treatment Details
          </Typography>
          <Box sx={{ position: "relative" }}>
            <textarea
              maxLength={200}
              value={gynTreatment}
              onChange={(e) => setGynTreatment(e.target.value)}
              rows={3}
              placeholder="Describe any treatment history..."
              style={{
                ...fieldBase,
                height: "auto",
                minHeight: "75px",
                resize: "none",
                padding: "10px",
                fontSize: "14px",
                borderRadius: "8px",
                fontFamily: "Lato, sans-serif",
              }}
            />
            <Typography
              sx={{
                position: "absolute",
                bottom: 8,
                right: 12,
                fontSize: "12px",
                color: gynTreatment.length > 180 ? "#E53E3E" : "#0097B2",
                fontWeight: 600,
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                px: 0.5,
                borderRadius: "4px",
              }}
            >
              {200 - gynTreatment.length} characters left
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Menstrual History Section */}
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#006C7F",
              mb: 2.5,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span
              style={{
                width: "4px",
                height: "16px",
                backgroundColor: "#0097B2",
                borderRadius: "2px",
                display: "inline-block",
              }}
            ></span>
            Menstrual History
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 2.5,
              mb: 2.5,
            }}
          >
            {[
              {
                label: "Menarche (Age)",
                value: gynMenarche,
                setter: setGynMenarche,
                type: "input",
                placeholder: "e.g. 12",
              },
              {
                label: "Cycle",
                value: gynCycle,
                setter: setGynCycle,
                type: "select",
                options: ["", "Regular", "Irregular", "Early", "Delayed"],
                placeholder: "Select cycle regularity...",
              },
              {
                label: "Cycle Duration",
                value: gynCycleDuration,
                setter: setGynCycleDuration,
                type: "select",
                options: [
                  "",
                  "Less than 3 days",
                  "3-7 days",
                  "More than 7 days",
                ],
                placeholder: "Select duration...",
              },
              {
                label: "Flow",
                value: gynFlow,
                setter: setGynFlow,
                type: "select",
                options: ["", "Scanty", "Normal", "Heavy", "None"],
                placeholder: "Select flow...",
              },
              {
                label: "Cramps",
                value: gynCramps,
                setter: setGynCramps,
                type: "select",
                options: ["", "None", "Mild", "Moderate", "Severe"],
                placeholder: "Select cramps...",
              },
            ].map(({ label, value, setter, type, options, placeholder }) => (
              <Box
                key={label}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 600, color: "#555" }}
                >
                  {label}
                </Typography>
                {type === "select" ? (
                  <select
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    style={{
                      ...fieldBase,
                      fontSize: "14px",
                      height: "34px",
                      borderRadius: "8px",
                      padding: "0 10px",
                      cursor: "pointer",
                    }}
                  >
                    {options.map((o) => (
                      <option key={o} value={o}>
                        {o || placeholder}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    style={{
                      ...fieldBase,
                      fontSize: "14px",
                      height: "34px",
                      borderRadius: "8px",
                      padding: "0 10px",
                    }}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                  />
                )}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#4A5568" }}
            >
              Menstrual Problems
            </Typography>
            <Box sx={{ position: "relative" }}>
              <textarea
                maxLength={200}
                value={gynMenstrualProblems}
                onChange={(e) => setGynMenstrualProblems(e.target.value)}
                rows={3}
                placeholder="Describe any menstrual issues (e.g., dysmenorrhea, heavy bleeding)..."
                style={{
                  ...fieldBase,
                  height: "auto",
                  minHeight: "75px",
                  resize: "none",
                  padding: "10px",
                  fontSize: "14px",
                  borderRadius: "8px",
                  fontFamily: "Lato, sans-serif",
                }}
              />
              <Typography
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 12,
                  fontSize: "12px",
                  color:
                    gynMenstrualProblems.length > 180 ? "#E53E3E" : "#0097B2",
                  fontWeight: 600,
                  backgroundColor: "rgba(255, 255, 255, 0.85)",
                  px: 0.5,
                  borderRadius: "4px",
                }}
              >
                {200 - gynMenstrualProblems.length} characters left
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Birth Control Section */}
        <Box
          sx={{
            opacity: isBirthControlActive ? 1 : 0.45,
            pointerEvents: isBirthControlActive ? "auto" : "none",
            transition: "opacity 0.25s ease",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: isBirthControlActive ? "#006C7F" : "#8A9A9D",
              mb: 2,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span
              style={{
                width: "4px",
                height: "16px",
                backgroundColor: isBirthControlActive ? "#0097B2" : "#A3B6B9",
                borderRadius: "2px",
                display: "inline-block",
              }}
            ></span>
            Birth Control
          </Typography>

          {/* None Toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: gynBirthControl.none ? "#FFF5F5" : "#F7FAFC",
              border: gynBirthControl.none
                ? "1px solid #FEB2B2"
                : "1px solid #E2E8F0",
              transition: "all 0.2s ease",
            }}
          >
            <Checkbox
              checked={gynBirthControl.none}
              onChange={(e) =>
                setGynBirthControl((p) => ({ ...p, none: e.target.checked }))
              }
              size="small"
              sx={{
                p: 0,
                color: "#0097B2",
                "&.Mui-checked": { color: "#0097B2" },
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: gynBirthControl.none ? "#C53030" : "#2D3748",
                }}
              >
                No Birth Control Used (None)
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#718096" }}>
                Select this if the patient is currently not using any
                contraception method.
              </Typography>
            </Box>
          </Box>

          {/* Grid of Birth Control Options */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              opacity: gynBirthControl.none ? 0.45 : 1,
              pointerEvents: gynBirthControl.none ? "none" : "auto",
              transition: "opacity 0.25s ease",
            }}
          >
            {[
              {
                key: "pills",
                label: "Pills",
                text: gynPillsText,
                setText: setGynPillsText,
                placeholder: "Brand or formulation info",
              },
              { key: "condoms", label: "Condoms", text: null },
              {
                key: "btl",
                label: "BTL",
                text: gynBtlText,
                setText: setGynBtlText,
                placeholder: "Bilateral Tubal Ligation details",
              },
              {
                key: "contraceptiveRing",
                label: "Contraceptive Ring",
                text: null,
              },
              {
                key: "vasectomy",
                label: "Vasectomy",
                text: gynVasectomyText,
                setText: setGynVasectomyText,
                placeholder: "Procedure details/date",
              },
              {
                key: "implanon",
                label: "Implanon",
                text: gynImplanonText,
                setText: setGynImplanonText,
                placeholder: "Insertion details/date",
              },
              {
                key: "attemptingPregnancy",
                label: "Attempting Pregnancy",
                text: null,
              },
              {
                key: "others",
                label: "Others",
                text: gynOthersText,
                setText: setGynOthersText,
                placeholder: "Specify other birth control method",
              },
            ].map(({ key, label, text, setText, placeholder }) => {
              const isChecked = gynBirthControl[key];
              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 1.5,
                    borderRadius: "8px",
                    border: isChecked
                      ? "1.5px solid #0097B2"
                      : "1px solid #E2E8F0",
                    backgroundColor: isChecked ? "#F0FAFB" : "white",
                    transition: "all 0.2s ease",
                    justifyContent: "center",
                    boxShadow: isChecked
                      ? "0 2px 8px rgba(0, 151, 178, 0.05)"
                      : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) =>
                        setGynBirthControl((p) => ({
                          ...p,
                          [key]: e.target.checked,
                        }))
                      }
                      size="small"
                      sx={{
                        p: 0,
                        color: "#0097B2",
                        "&.Mui-checked": { color: "#0097B2" },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: isChecked ? 600 : 500,
                        color: isChecked ? "#006C7F" : "#333",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  {text !== null && isChecked && (
                    <input
                      style={{
                        ...fieldBase,
                        fontSize: "14px",
                        height: "28px",
                        borderRadius: "6px",
                        padding: "0 8px",
                        borderColor: "#0097B2",
                      }}
                      placeholder={placeholder}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                    />
                  )}
                </Box>
              );
            })}

            {/* IUD Details (Spans whole row) */}
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: 1.5,
                borderRadius: "8px",
                border: gynBirthControl.iud
                  ? "1.5px solid #0097B2"
                  : "1px solid #E2E8F0",
                backgroundColor: gynBirthControl.iud ? "#F0FAFB" : "white",
                transition: "all 0.2s ease",
                boxShadow: gynBirthControl.iud
                  ? "0 2px 8px rgba(0, 151, 178, 0.05)"
                  : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Checkbox
                  checked={gynBirthControl.iud}
                  onChange={(e) =>
                    setGynBirthControl((p) => ({ ...p, iud: e.target.checked }))
                  }
                  size="small"
                  sx={{
                    p: 0,
                    color: "#0097B2",
                    "&.Mui-checked": { color: "#0097B2" },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: gynBirthControl.iud ? 600 : 500,
                    color: gynBirthControl.iud ? "#006C7F" : "#333",
                  }}
                >
                  IUD (Intrauterine Device)
                </Typography>
              </Box>

              {gynBirthControl.iud && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1.2fr 1fr 1fr",
                    },
                    gap: 2,
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: 600, color: "#666" }}
                    >
                      IUD Type
                    </Typography>
                    <select
                      value={gynIudType}
                      onChange={(e) => setGynIudType(e.target.value)}
                      style={{
                        ...fieldBase,
                        fontSize: "11.5px",
                        height: "30px",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      <option value="">Select type...</option>
                      <option value="ParaGard">ParaGard</option>
                      <option value="Mirena">Mirena</option>
                      <option value="Kyleena">Kyleena</option>
                      <option value="Skyla">Skyla</option>
                    </select>
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: 600, color: "#666" }}
                    >
                      Insertion Date
                    </Typography>
                    <input
                      type="date"
                      style={{
                        ...fieldBase,
                        fontSize: "11.5px",
                        height: "30px",
                        borderRadius: "6px",
                      }}
                      value={gynIudInsertDate}
                      onChange={(e) => setGynIudInsertDate(e.target.value)}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: 600, color: "#666" }}
                    >
                      Removal Date
                    </Typography>
                    <input
                      type="date"
                      style={{
                        ...fieldBase,
                        fontSize: "11.5px",
                        height: "30px",
                        borderRadius: "6px",
                      }}
                      value={gynIudRemovalDate}
                      onChange={(e) => setGynIudRemovalDate(e.target.value)}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <SaveBtn onClick={saveGynHistory} />
      </Box>
    );
  };

  const renderPastPregnancies = () => (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#666" }}>More</Typography>
        <select
          value={pregnancyMoreRows}
          onChange={(e) => setPregnancyMoreRows(Number(e.target.value))}
          style={{
            ...fieldBase,
            width: 70,
            height: "28px",
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          <option value={0}>—</option>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </Box>
      {Array.from({ length: 5 + pregnancyMoreRows }, (_, i) => (
        <Box
          key={`preg-${i}`}
          sx={{
            border: "1px solid #E1E1E1",
            borderRadius: "8px",
            p: 1,
            mb: 1,
            backgroundColor: "#fafcfc",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 1,
            }}
          >
            <Box>
              <span style={labelStyle}>Date</span>
              <input
                type="date"
                value={pregnancyRows[i].date}
                onChange={(e) => updatePregnancyRow(i, "date", e.target.value)}
                style={{ ...fieldBase, fontSize: "14px", height: "28px" }}
              />
            </Box>
            <Box>
              <span style={labelStyle}>GA Weeks</span>
              <input
                value={pregnancyRows[i].ga_weeks}
                onChange={(e) =>
                  updatePregnancyRow(i, "ga_weeks", e.target.value)
                }
                style={{ ...fieldBase, fontSize: "14px", height: "28px" }}
              />
            </Box>
            <Box>
              <span style={labelStyle}>Birth Weight</span>
              <input
                value={pregnancyRows[i].birth_weight}
                onChange={(e) =>
                  updatePregnancyRow(i, "birth_weight", e.target.value)
                }
                style={{ ...fieldBase, fontSize: "14px", height: "28px" }}
              />
            </Box>
            <Box>
              <span style={labelStyle}>Type of Delivery</span>
              <select
                value={pregnancyRows[i].type_of_delivery}
                onChange={(e) =>
                  updatePregnancyRow(i, "type_of_delivery", e.target.value)
                }
                style={{
                  ...fieldBase,
                  fontSize: "14px",
                  height: "28px",
                  cursor: "pointer",
                }}
              >
                <option value=""></option>
                <option>Normal</option>
                <option>C-Section</option>
                <option>Forceps</option>
                <option>Vacuum</option>
              </select>
            </Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Box>
              <span style={labelStyle}>Place of Delivery</span>
              <select
                value={pregnancyRows[i].place_of_delivery}
                onChange={(e) =>
                  updatePregnancyRow(i, "place_of_delivery", e.target.value)
                }
                style={{
                  ...fieldBase,
                  fontSize: "14px",
                  height: "28px",
                  cursor: "pointer",
                }}
              >
                <option value=""></option>
                <option>Hospital</option>
                <option>Home</option>
                <option>Other</option>
              </select>
            </Box>
            <Box>
              <span style={labelStyle}>Preterm Labor</span>
              <select
                value={pregnancyRows[i].preterm_labor}
                onChange={(e) =>
                  updatePregnancyRow(i, "preterm_labor", e.target.value)
                }
                style={{
                  ...fieldBase,
                  fontSize: "14px",
                  height: "28px",
                  cursor: "pointer",
                }}
              >
                <option value=""></option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </Box>
            <Box>
              <span style={labelStyle}>Outcome</span>
              <select
                value={pregnancyRows[i].outcome}
                onChange={(e) =>
                  updatePregnancyRow(i, "outcome", e.target.value)
                }
                style={{
                  ...fieldBase,
                  fontSize: "14px",
                  height: "28px",
                  cursor: "pointer",
                }}
              >
                <option value=""></option>
                <option>Successful</option>
                <option>IUFD</option>
                <option>Living</option>
                <option>Still Birth</option>
                <option>Deceased</option>
                <option>FTND</option>
                <option>PTND</option>
                <option>LSCS</option>
                <option>MTP</option>
                <option>Abortion</option>
              </select>
            </Box>
            <Box>
              <span style={labelStyle}>Comments</span>
              <textarea
                value={pregnancyRows[i].comments}
                onChange={(e) =>
                  updatePregnancyRow(i, "comments", e.target.value)
                }
                style={{
                  ...fieldBase,
                  height: "28px",
                  resize: "none",
                  fontSize: "14px",
                  padding: "4px 6px",
                  lineHeight: "1.2",
                }}
              />
            </Box>
          </Box>
        </Box>
      ))}
      <SaveBtn onClick={savePastPregnancies} />
    </Box>
  );

  const renderFamilyHistory = () => {
    const stickyLabelSx = (bg = "white") => ({
      fontSize: "14px",
      fontWeight: 600,
      color: "#555",
      py: "5px",
      borderRight: "1px solid #E1E1E1",
      whiteSpace: "normal",
      wordBreak: "break-word",
      position: "sticky",
      left: 0,
      backgroundColor: bg,
      zIndex: 1,
    });
    const isLastCell = (type, i) => {
      if (familyExtraCols.length > 0)
        return type === "extra" && i === familyExtraCols.length - 1;
      return type === "def" && i === familyDefaultSelectCols.length - 1;
    };
    const cellBorder = (type, i) =>
      isLastCell(type, i) ? "none" : "1px solid #E1E1E1";
    const inputSx = {
      ...fieldBase,
      fontSize: "14px",
      height: "26px",
      width: "100%",
    };
    const selectSx = {
      ...fieldBase,
      fontSize: "14px",
      height: "26px",
      cursor: "pointer",
      width: "100%",
    };
    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={noFamilyIllness}
              onChange={(e) => setNoFamilyIllness(e.target.checked)}
              size="small"
              sx={{
                p: 0,
                color: "#0097B2",
                "&.Mui-checked": { color: "#0097B2" },
              }}
            />
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
            >
              No Family History Illness
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {familyExtraCols.length > 0 && (
              <Box
                onClick={removeFamilyCol}
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#E53935",
                  cursor: "pointer",
                  textDecoration: "underline",
                  userSelect: "none",
                }}
              >
                Less
              </Box>
            )}
            {familyExtraCols.length < 2 && (
              <Box
                onClick={addFamilyCol}
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#0097B2",
                  cursor: "pointer",
                  textDecoration: "underline",
                  userSelect: "none",
                }}
              >
                More
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            opacity: noFamilyIllness ? 0.4 : 1,
            pointerEvents: noFamilyIllness ? "none" : "auto",
          }}
        >
          <TableContainer
            sx={{
              border: "1px solid #E1E1E1",
              borderRadius: "8px",
              mb: 2,
              overflowX: "auto",
            }}
          >
            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                minWidth: `${160 + (2 + 2 + familyExtraCols.length) * 100}px`,
              }}
            >
              <colgroup>
                <col style={{ width: "160px" }} />
                {Array.from({ length: 2 + 2 + familyExtraCols.length }).map(
                  (_, i) => (
                    <col key={i} style={{ width: "100px" }} />
                  ),
                )}
              </colgroup>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F5F7F8" }}>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#666",
                      textTransform: "uppercase",
                      py: "7px",
                      borderRight: "1px solid #E1E1E1",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#F5F7F8",
                      zIndex: 2,
                    }}
                  />
                  {familyStaticCols.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#444",
                        textTransform: "uppercase",
                        py: "7px",
                        textAlign: "center",
                        borderRight: "1px solid #E1E1E1",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                  {familyDefaultSelectCols.map((col, i) => {
                    const isLast =
                      familyExtraCols.length === 0 &&
                      i === familyDefaultSelectCols.length - 1;
                    return (
                      <TableCell
                        key={`def-${i}`}
                        sx={{
                          py: "4px",
                          borderRight: isLast ? "none" : "1px solid #E1E1E1",
                          textAlign: "center",
                        }}
                      >
                        <select
                          value={col.relation}
                          onChange={(e) =>
                            updateDefaultSelectCol(
                              i,
                              "relation",
                              e.target.value,
                            )
                          }
                          style={{
                            ...fieldBase,
                            fontSize: "12px",
                            height: "26px",
                            cursor: "pointer",
                            width: "92px",
                          }}
                        >
                          <option value="">Select</option>
                          {FAMILY_RELATION_OPTIONS.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </TableCell>
                    );
                  })}
                  {familyExtraCols.map((col, i) => (
                    <TableCell
                      key={`extra-${i}`}
                      sx={{
                        py: "4px",
                        borderRight:
                          i < familyExtraCols.length - 1
                            ? "1px solid #E1E1E1"
                            : "none",
                        textAlign: "center",
                      }}
                    >
                      <select
                        value={col.relation}
                        onChange={(e) =>
                          updateExtraCol(i, "relation", e.target.value)
                        }
                        style={{
                          ...fieldBase,
                          fontSize: "12px",
                          height: "26px",
                          cursor: "pointer",
                          width: "92px",
                        }}
                      >
                        <option value="">Select</option>
                        {FAMILY_RELATION_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Age row */}
                <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
                  <TableCell sx={stickyLabelSx("#FAFAFA")}>Age</TableCell>
                  {familyStaticCols.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{ p: "4px", borderRight: "1px solid #E1E1E1" }}
                    >
                      <input
                        style={inputSx}
                        value={familyStaticData[col.key].age}
                        onChange={(e) =>
                          updateStaticCol(col.key, "age", e.target.value)
                        }
                      />
                    </TableCell>
                  ))}
                  {familyDefaultSelectCols.map((col, i) => (
                    <TableCell
                      key={`def-age-${i}`}
                      sx={{ p: "4px", borderRight: cellBorder("def", i) }}
                    >
                      <input
                        style={inputSx}
                        value={col.age}
                        onChange={(e) =>
                          updateDefaultSelectCol(i, "age", e.target.value)
                        }
                      />
                    </TableCell>
                  ))}
                  {familyExtraCols.map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{ p: "4px", borderRight: cellBorder("extra", i) }}
                    >
                      <input
                        style={inputSx}
                        value={col.age}
                        onChange={(e) =>
                          updateExtraCol(i, "age", e.target.value)
                        }
                      />
                    </TableCell>
                  ))}
                </TableRow>
                {/* Living Status row */}
                <TableRow>
                  <TableCell sx={stickyLabelSx("white")}>
                    Living Status
                  </TableCell>
                  {familyStaticCols.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{ p: "4px", borderRight: "1px solid #E1E1E1" }}
                    >
                      <select
                        style={selectSx}
                        value={familyStaticData[col.key].livingStatus}
                        onChange={(e) =>
                          updateStaticCol(
                            col.key,
                            "livingStatus",
                            e.target.value,
                          )
                        }
                      >
                        <option value=""></option>
                        <option>Living</option>
                        <option>Deceased</option>
                        <option>Unknown</option>
                      </select>
                    </TableCell>
                  ))}
                  {familyDefaultSelectCols.map((col, i) => (
                    <TableCell
                      key={`def-ls-${i}`}
                      sx={{ p: "4px", borderRight: cellBorder("def", i) }}
                    >
                      <select
                        style={selectSx}
                        value={col.livingStatus}
                        onChange={(e) =>
                          updateDefaultSelectCol(
                            i,
                            "livingStatus",
                            e.target.value,
                          )
                        }
                      >
                        <option value=""></option>
                        <option>Living</option>
                        <option>Deceased</option>
                        <option>Unknown</option>
                      </select>
                    </TableCell>
                  ))}
                  {familyExtraCols.map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{ p: "4px", borderRight: cellBorder("extra", i) }}
                    >
                      <select
                        style={selectSx}
                        value={col.livingStatus}
                        onChange={(e) =>
                          updateExtraCol(i, "livingStatus", e.target.value)
                        }
                      >
                        <option value=""></option>
                        <option>Living</option>
                        <option>Deceased</option>
                        <option>Unknown</option>
                      </select>
                    </TableCell>
                  ))}
                </TableRow>
                {/* Disease rows */}
                {FAMILY_DISEASES.map((disease, di) => (
                  <TableRow
                    key={disease}
                    sx={{ backgroundColor: di % 2 === 0 ? "#FAFAFA" : "white" }}
                  >
                    <TableCell
                      sx={{
                        ...stickyLabelSx(di % 2 === 0 ? "#FAFAFA" : "white"),
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#444",
                      }}
                    >
                      {disease}
                    </TableCell>
                    {familyStaticCols.map((col) => (
                      <TableCell
                        key={col.key}
                        sx={{
                          p: "4px",
                          textAlign: "center",
                          borderRight: "1px solid #E1E1E1",
                        }}
                      >
                        <Checkbox
                          checked={!!familyStaticData[col.key].checked[disease]}
                          onChange={() => toggleStaticDisease(col.key, disease)}
                          size="small"
                          sx={{
                            p: 0,
                            color: "#0097B2",
                            "&.Mui-checked": { color: "#0097B2" },
                          }}
                        />
                      </TableCell>
                    ))}
                    {familyDefaultSelectCols.map((col, i) => (
                      <TableCell
                        key={`def-d-${i}`}
                        sx={{
                          p: "4px",
                          textAlign: "center",
                          borderRight: cellBorder("def", i),
                        }}
                      >
                        <Checkbox
                          checked={!!col.checked[disease]}
                          onChange={() =>
                            toggleDefaultSelectDisease(i, disease)
                          }
                          size="small"
                          sx={{
                            p: 0,
                            color: "#0097B2",
                            "&.Mui-checked": { color: "#0097B2" },
                          }}
                        />
                      </TableCell>
                    ))}
                    {familyExtraCols.map((col, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          p: "4px",
                          textAlign:
                            disease === "Malignancy" ? "left" : "center",
                          borderRight: cellBorder("extra", i),
                        }}
                      >
                        {disease === "Malignancy" ? (
                          <input
                            style={inputSx}
                            value={col.extraInputs[disease] || ""}
                            onChange={(e) =>
                              updateExtraColDiseaseInput(
                                i,
                                disease,
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          <Checkbox
                            checked={!!col.checked[disease]}
                            onChange={() => toggleExtraDisease(i, disease)}
                            size="small"
                            sx={{
                              p: 0,
                              color: "#0097B2",
                              "&.Mui-checked": { color: "#0097B2" },
                            }}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mb: 2 }}>
            <label style={labelStyle}>Comments</label>
            <textarea
              rows={3}
              value={familyComments}
              onChange={(e) => setFamilyComments(e.target.value)}
              style={{
                ...fieldBase,
                height: "auto",
                resize: "vertical",
                padding: "6px",
                fontSize: "14px",
              }}
              placeholder="Add comments..."
            />
          </Box>
        </Box>
        <SaveBtn onClick={saveFamilyHistory} />
      </Box>
    );
  };

  const renderSocialHistory = () => (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box>
          <label style={labelStyle}>Marital Status</label>
          <select
            style={{ ...fieldBase, cursor: "pointer" }}
            value={socialMaritalStatus}
            onChange={(e) => setSocialMaritalStatus(e.target.value)}
          >
            <option value="">Select</option>
            {[
              "Single",
              "Married",
              "UnMarried",
              "Divorced",
              "Widowed",
              "Separated",
              "Patner",
            ].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Box>
        <Box>
          <label style={labelStyle}>Education</label>
          <select
            style={{ ...fieldBase, cursor: "pointer" }}
            value={socialEducation}
            onChange={(e) => setSocialEducation(e.target.value)}
          >
            <option value="">Select</option>
            {[
              "No Formal Education",
              "Primary",
              "Secondary",
              "Diploma",
              "Post Diploma",
              "Graduate",
              "Post Graduate",
              "Doctorate",
              "Research",
            ].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box>
          <label style={labelStyle}>Occupation</label>
          <input
            style={fieldBase}
            value={socialOccupation}
            onChange={(e) => setSocialOccupation(e.target.value)}
            placeholder="Enter occupation"
          />
        </Box>
        <Box>
          <label style={labelStyle}>Industry</label>
          <select
            style={{ ...fieldBase, cursor: "pointer" }}
            value={socialIndustry}
            onChange={(e) => setSocialIndustry(e.target.value)}
          >
            <option value=""></option>
            {[
              "Education",
              "Medical",
              "Electrical",
              "Electronics",
              "Information and Technology",
              "Manufacturing Industry",
              "Banking",
              "Media and Entertainment",
              "Food industries",
              "Transport",
              "Agriculture",
              "Science and Research",
              "Support Services",
            ].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box>
          <label style={labelStyle}>General Stress Level</label>
          <select
            style={{ ...fieldBase, cursor: "pointer" }}
            value={socialStress}
            onChange={(e) => setSocialStress(e.target.value)}
          >
            <option value="">Select</option>
            {["Low", "Medium", "High", "Very High"].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Box>
        <Box>
          <label style={labelStyle}>Diet</label>
          <select
            style={{ ...fieldBase, cursor: "pointer" }}
            value={socialDiet}
            onChange={(e) => setSocialDiet(e.target.value)}
          >
            <option value="">Select</option>
            {[
              "Regular",
              "Vegetarian",
              "Non-Vegetarian",
              "1800 Calorie",
              "Diebetic or Calorie controlled",
              "No Added Salt",
              "Vegan",
              "Eggetarian",
              "Mixed",
              "Other",
            ].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Box>
        <Box>
          <label style={labelStyle}>Caffeine Intake</label>
          <select
            style={{ ...fieldBase, cursor: "pointer" }}
            value={socialCaffeineIntake}
            onChange={(e) => setSocialCaffeineIntake(e.target.value)}
          >
            <option value="">Select</option>
            {[
              "None",
              "Occasional",
              "Modrate",
              "Heavy",
              "1 cup/day",
              "2-3 cups/day",
              "4-5 cups/day",
              "> 5 cups/day",
            ].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 700,
          color: "#0097B2",
          textTransform: "uppercase",
          mb: 1,
          letterSpacing: "0.5px",
        }}
      >
        Tobacco & Substance Use
      </Typography>
      {/* Tobacco Smoking */}
      <Box
        sx={{
          border: "1px solid #E1E1E1",
          borderRadius: "8px",
          p: 1.2,
          mb: 1.2,
          backgroundColor: "#fafcfc",
        }}
      >
        <Box
          sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 1.5 }}
        >
          <Box>
            <label style={labelStyle}>Tobacco — Smoking Status</label>
            <select
              style={{ ...fieldBase, cursor: "pointer" }}
              value={socialTobaccoSmoking}
              onChange={(e) => setSocialTobaccoSmoking(e.target.value)}
            >
              <option value=""></option>
              {[
                "current every day smoker",
                "current some day smoker",
                "former smoker",
                "never smoker",
                "smoking, current status unknown",
                "unknown if ever smoked",
                "heavy tobacco smoker",
                "light tobacco smoker",
              ].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Box>
          <Box>
            <label style={labelStyle}>Quantity</label>
            <select
              style={{ ...fieldBase, cursor: "pointer" }}
              value={socialTobaccoSmokingQty}
              onChange={(e) => setSocialTobaccoSmokingQty(e.target.value)}
            >
              <option value="">Select</option>
              {TOBACCO_SMOKING_QTY.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Box>
          <Box>
            <label style={labelStyle}>Duration</label>
            <input
              style={fieldBase}
              value={socialTobaccoSmokingDuration}
              onChange={(e) => setSocialTobaccoSmokingDuration(e.target.value)}
              placeholder="e.g. 5 years"
            />
          </Box>
        </Box>
      </Box>
      {/* Tobacco Chewing */}
      <Box
        sx={{
          border: "1px solid #E1E1E1",
          borderRadius: "8px",
          p: 1.2,
          mb: 1.2,
          backgroundColor: "#fafcfc",
        }}
      >
        <Box
          sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 1.5 }}
        >
          <Box>
            <label style={labelStyle}>Tobacco — Chewing Status</label>
            <select
              style={{ ...fieldBase, cursor: "pointer" }}
              value={socialTobaccoChewing}
              onChange={(e) => setSocialTobaccoChewing(e.target.value)}
            >
              <option value="">Select</option>
              {["Never", "Yes", "Former", "Quit"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Box>
          <Box>
            <label style={labelStyle}>Quantity</label>
            <select
              style={{ ...fieldBase, cursor: "pointer" }}
              value={socialTobaccoChewingQty}
              onChange={(e) => setSocialTobaccoChewingQty(e.target.value)}
            >
              <option value="">Select</option>
              {TOBACCO_CHEWING_QTY.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Box>
          <Box>
            <label style={labelStyle}>Duration</label>
            <input
              style={fieldBase}
              value={socialTobaccoChewingDuration}
              onChange={(e) => setSocialTobaccoChewingDuration(e.target.value)}
              placeholder="e.g. 2 years"
            />
          </Box>
        </Box>
      </Box>
      {/* Alcohol */}
      <Box
        sx={{
          border: "1px solid #E1E1E1",
          borderRadius: "8px",
          p: 1.2,
          mb: 1.2,
          backgroundColor: "#fafcfc",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr 1fr",
            gap: 1.5,
          }}
        >
          <Box>
            <label style={labelStyle}>Alcohol</label>
            <select
              style={{ ...fieldBase, cursor: "pointer" }}
              value={socialAlcohol}
              onChange={(e) => setSocialAlcohol(e.target.value)}
            >
              <option value="">Select</option>
              {["No", "Yes", "Former", "Quit"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Box>
          <Box
            sx={{
              opacity: socialAlcohol === "Yes" ? 1 : 0.4,
              pointerEvents: socialAlcohol === "Yes" ? "auto" : "none",
            }}
          >
            <label style={labelStyle}>If Yes</label>
            <select
              style={{ ...fieldBase, cursor: "pointer" }}
              value={socialAlcoholIfYes}
              onChange={(e) => setSocialAlcoholIfYes(e.target.value)}
            >
              <option value="">Select</option>
              {["Occasional", "Social", "Weekly", "Daily", "Heavy"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Box>
          <Box>
            <label style={labelStyle}>Duration</label>
            <input
              style={fieldBase}
              value={socialAlcoholDuration}
              onChange={(e) => setSocialAlcoholDuration(e.target.value)}
              placeholder="e.g. 3 years"
            />
          </Box>
        </Box>
      </Box>
      {/* Drugs */}
      <Box
        sx={{
          border: "1px solid #E1E1E1",
          borderRadius: "8px",
          p: 1.2,
          mb: 1.5,
          backgroundColor: "#fafcfc",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr 1fr",
            gap: 1.5,
          }}
        >
          <Box>
            <label style={labelStyle}>Drugs</label>
            <select
              style={{ ...fieldBase, cursor: "pointer" }}
              value={socialDrugs}
              onChange={(e) => setSocialDrugs(e.target.value)}
            >
              <option value="">Select</option>
              {["No", "Yes", "Former", "Quit"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Box>
          <Box
            sx={{
              opacity: socialDrugs === "Yes" ? 1 : 0.4,
              pointerEvents: socialDrugs === "Yes" ? "auto" : "none",
            }}
          >
            <label style={labelStyle}>If Yes (select all)</label>
            <Box
              sx={{
                border: "1px solid #D0D8DA",
                borderRadius: "6px",
                backgroundColor: "white",
                maxHeight: "80px",
                overflowY: "auto",
                p: "4px 6px",
              }}
            >
              {DRUG_OPTIONS.map((drug) => (
                <Box
                  key={drug}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Checkbox
                    checked={socialDrugsSelected.includes(drug)}
                    onChange={() =>
                      setSocialDrugsSelected((p) =>
                        p.includes(drug)
                          ? p.filter((d) => d !== drug)
                          : [...p, drug],
                      )
                    }
                    size="small"
                    sx={{
                      p: 0,
                      color: "#0097B2",
                      "&.Mui-checked": { color: "#0097B2" },
                    }}
                  />
                  <Typography sx={{ fontSize: "14px", color: "#333" }}>
                    {drug}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box>
            <label style={labelStyle}>Duration</label>
            <input
              style={fieldBase}
              value={socialDrugsDuration}
              onChange={(e) => setSocialDrugsDuration(e.target.value)}
              placeholder="e.g. 1 year"
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <label style={labelStyle}>Exercise</label>
        <textarea
          value={socialExercise}
          onChange={(e) => setSocialExercise(e.target.value)}
          maxLength={200}
          placeholder="Describe exercise routine..."
          style={{
            ...fieldBase,
            height: "50px",
            resize: "none",
            padding: "6px",
            fontSize: "14px",
          }}
        />
        <Box
          sx={{
            textAlign: "right",
            fontSize: "12px",
            color: "#999",
            mt: "2px",
          }}
        >
          {socialExercise.length}/200
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <label style={labelStyle}>Comments</label>
        <textarea
          value={socialComments}
          onChange={(e) => setSocialComments(e.target.value)}
          placeholder="Add comments..."
          style={{
            ...fieldBase,
            height: "50px",
            resize: "none",
            padding: "6px",
            fontSize: "14px",
          }}
        />
      </Box>
      <SaveBtn onClick={saveSocialHistory} />
    </Box>
  );

  const renderHpi = () => (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography sx={{ fontSize: "14px", color: "#999" }}>
        Add form not available for HPI. Use View to see recorded data.
      </Typography>
    </Box>
  );

  const renderRos = () => (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography sx={{ fontSize: "14px", color: "#999" }}>
        Add form not available for ROS. Use View to see recorded data.
      </Typography>
    </Box>
  );

  const renderImpressionPlan = () => (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography sx={{ fontSize: "14px", color: "#999" }}>
        Add form not available for Impression Plan. Use View to see recorded
        data.
      </Typography>
    </Box>
  );

  const renderInstructions = () => (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography sx={{ fontSize: "14px", color: "#999" }}>
        Add form not available for Instructions. Use View to see recorded data.
      </Typography>
    </Box>
  );

  const renderReferrals = () => (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography sx={{ fontSize: "14px", color: "#999" }}>
        Add form not available for Referrals. Use View to see recorded data.
      </Typography>
    </Box>
  );

  // ── Vitals state (add form) ──
  const [vitalsBpS, setVitalsBpS] = useState("");
  const [vitalsBpD, setVitalsBpD] = useState("");
  const [vitalsHeight, setVitalsHeight] = useState("");
  const [vitalsWeight, setVitalsWeight] = useState("");
  const [vitalsFever, setVitalsFever] = useState("");
  const [vitalsBmi, setVitalsBmi] = useState("");
  const [vitalsPulseRate, setVitalsPulseRate] = useState("");
  const [vitalsHeartRate, setVitalsHeartRate] = useState("");
  const [vitalsPainLevel, setVitalsPainLevel] = useState("");
  const [vitalsSpo2, setVitalsSpo2] = useState("");
  const [vitalsAlcohol, setVitalsAlcohol] = useState(false);
  const [vitalsTobacco, setVitalsTobacco] = useState(false);
  const [vitalsSmoking, setVitalsSmoking] = useState(false);
  const [vitalsComments, setVitalsComments] = useState("");

  const saveVitals = async () => {
    if (!appointmentData?.id) {
      showToast("Please select an appointment first.", "ERROR");
      return;
    }
    const payload = {
      appointment: {
        bp_s: vitalsBpS !== "" ? Number(vitalsBpS) : null,
        bp_d: vitalsBpD !== "" ? Number(vitalsBpD) : null,
        height: vitalsHeight !== "" ? Number(vitalsHeight) : null,
        weight: vitalsWeight !== "" ? Number(vitalsWeight) : null,
        fever: vitalsFever !== "" ? Number(vitalsFever) : null,
        bmi: vitalsBmi !== "" ? Number(vitalsBmi) : null,
        pulse_rate: vitalsPulseRate !== "" ? Number(vitalsPulseRate) : null,
        heart_rate: vitalsHeartRate !== "" ? Number(vitalsHeartRate) : null,
        pain_level: vitalsPainLevel !== "" ? Number(vitalsPainLevel) : null,
        spo2: vitalsSpo2 !== "" ? Number(vitalsSpo2) : null,
        alcohol: vitalsAlcohol,
        tobacco: vitalsTobacco,
        smoking: vitalsSmoking,
        comments: vitalsComments,
      },
    };
    const result = await updatePatientAppointment(payload, appointmentData.id);
    showToast(
      result?.status?.message || result?.status?.status || "Saved",
      result?.status?.status || "SUCCESS",
    );
    if (result?.status?.status === "SUCCESS") {
      dispatch(
        setAppointmentData({ ...appointmentData, ...payload.appointment }),
      );
    }
  };

  const renderVitals = () => (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        {[
          {
            label: "BP Systolic (mmHg)",
            value: vitalsBpS,
            setter: setVitalsBpS,
          },
          {
            label: "BP Diastolic (mmHg)",
            value: vitalsBpD,
            setter: setVitalsBpD,
          },
          {
            label: "Height (cm)",
            value: vitalsHeight,
            setter: setVitalsHeight,
          },
          {
            label: "Weight (kg)",
            value: vitalsWeight,
            setter: setVitalsWeight,
          },
          {
            label: "Temperature (°F)",
            value: vitalsFever,
            setter: setVitalsFever,
          },
          { label: "BMI", value: vitalsBmi, setter: setVitalsBmi },
          {
            label: "Pulse Rate (bpm)",
            value: vitalsPulseRate,
            setter: setVitalsPulseRate,
          },
          {
            label: "Heart Rate (bpm)",
            value: vitalsHeartRate,
            setter: setVitalsHeartRate,
          },
          {
            label: "Pain Level (0-10)",
            value: vitalsPainLevel,
            setter: setVitalsPainLevel,
          },
          { label: "SpO2 (%)", value: vitalsSpo2, setter: setVitalsSpo2 },
        ].map(({ label, value, setter }) => (
          <Box key={label}>
            <label style={labelStyle}>{label}</label>
            <input
              style={fieldBase}
              value={value}
              onChange={(e) => setter(e.target.value)}
              placeholder="—"
            />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
        {[
          { label: "Alcohol", value: vitalsAlcohol, setter: setVitalsAlcohol },
          { label: "Tobacco", value: vitalsTobacco, setter: setVitalsTobacco },
          { label: "Smoking", value: vitalsSmoking, setter: setVitalsSmoking },
        ].map(({ label, value, setter }) => (
          <Box
            key={label}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Checkbox
              checked={value}
              onChange={(e) => setter(e.target.checked)}
              size="small"
              sx={{
                p: 0,
                color: "#0097B2",
                "&.Mui-checked": { color: "#0097B2" },
              }}
            />
            <Typography sx={{ fontSize: "14px", color: "#333" }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ mb: 2 }}>
        <label style={labelStyle}>Comments</label>
        <textarea
          value={vitalsComments}
          onChange={(e) => setVitalsComments(e.target.value)}
          rows={3}
          style={{
            ...fieldBase,
            height: "auto",
            resize: "vertical",
            padding: "8px",
          }}
          placeholder="Add comments..."
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          sx={{ ...primaryBtnTeal, gap: 0.6, fontSize: "14px", p: 1.2 }}
          onClick={saveVitals}
        >
          <SaveIcon sx={{ width: 15, height: 15 }} /> Save
        </Button>
      </Box>
    </Box>
  );

  const tabContent = [
    renderChiefComplaints,
    renderAllergies,
    renderPastMedical,
    renderPastSurgical,
    renderPastDiagnostic,
    renderGynHistory,
    renderPastPregnancies,
    renderFamilyHistory,
    renderSocialHistory,
    renderHpi,
    renderRos,
    renderImpressionPlan,
    renderInstructions,
    renderReferrals,
    renderVitals,
  ];

  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        mt: 2,
        borderRadius: "10px",
        boxShadow: "none",
        border: "1px solid #E1E1E1",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "neutral.white",
          minHeight: "52px",
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            margin: "12px 0",
          },
          "& .MuiAccordionSummary-expandIconWrapper": { alignSelf: "center" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              backgroundColor: "neutral.blackBorder",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <MedicalInformationOutlinedIcon sx={{ color: "#0097B2" }} />
          </Box>
          <Typography fontWeight={600}>Clinical Data</Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {/* Tab buttons */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            p: 1.5,
            borderBottom: "1px solid #E1E1E1",
            borderTop: "1px solid #E1E1E1",
            backgroundColor: "#FAFCFC",
          }}
        >
          {TABS.map((tab, i) => (
            <Box
              key={tab}
              onClick={() => setActiveTab(i)}
              sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
                border:
                  activeTab === i
                    ? "1.5px solid #0097B2"
                    : "1.5px solid #D0D8DA",
                backgroundColor: activeTab === i ? "#0097B2" : "white",
                color: activeTab === i ? "white" : "#555",
                transition: "all 0.15s",
                userSelect: "none",
                "&:hover": {
                  borderColor: "#0097B2",
                  backgroundColor: activeTab === i ? "#0097B2" : "#E6F7FA",
                  color: activeTab === i ? "white" : "#0097B2",
                },
              }}
            >
              {tab}
            </Box>
          ))}
        </Box>

        {/* View / Add toggle */}
        {TAB_KEYS[activeTab] === "vitals" && (
          <Box sx={{ px: 1.5, pt: 1.5, mb: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                backgroundColor: "#E8F4F7",
                borderRadius: "999px",
                p: "3px",
              }}
            >
              {["View", "Add"].map((mode) => (
                <Box
                  key={mode}
                  onClick={() => setTabMode(mode.toLowerCase())}
                  sx={{
                    px: 2.5,
                    py: 0.5,
                    borderRadius: "999px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    userSelect: "none",
                    transition: "all 0.2s ease",
                    backgroundColor:
                      tabMode === mode.toLowerCase() ? "#0097B2" : "transparent",
                    color:
                      tabMode === mode.toLowerCase() ? "white" : "#555",
                    boxShadow:
                      tabMode === mode.toLowerCase()
                        ? "0 1px 4px rgba(0,151,178,0.3)"
                        : "none",
                  }}
                >
                  {mode}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Active tab content */}
        <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={28} sx={{ color: "#0097B2" }} />
            </Box>
          ) : tabMode === "add" ? (
            tabContent[activeTab]()
          ) : viewData?.value ||
            (TAB_KEYS[activeTab] === "hpi" && viewData) ||
            TAB_KEYS[activeTab] === "vitals" ? (
            <Box sx={{ p: 2 }}>
              {TAB_KEYS[activeTab] === "chief_complaints" &&
                (() => {
                  const v = viewData.value;
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {v.complaint && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              mb: 0.8,
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                            }}
                          >
                            Complaints
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              mt: 0.5,
                            }}
                          >
                            {v.complaint
                              .split(",")
                              .map((c) => c.trim())
                              .filter(Boolean)
                              .map((c) => (
                                <Box
                                  key={c}
                                  sx={{
                                    px: 1.2,
                                    py: 0.4,
                                    borderRadius: "20px",
                                    border: "1.5px solid #0097B2",
                                    backgroundColor: "#E6F7FA",
                                    fontSize: "14px",
                                    color: "#0097B2",
                                    fontWeight: 500,
                                  }}
                                >
                                  {c}
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      )}
                      {v.duration && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              mb: 0.8,
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                            }}
                          >
                            Duration
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            {v.duration}
                          </Typography>
                        </Box>
                      )}
                      {v.comments && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              mb: 0.8,
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                            }}
                          >
                            Comments
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#555",
                              lineHeight: 1.6,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {v.comments}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "allergies" &&
                (() => {
                  const v = viewData.value;
                  return (
                    <Box>
                      {v.nkda && (
                        <Box
                          sx={{
                            mb: 2,
                            p: 1.5,
                            backgroundColor: "#FFF8E1",
                            border: "1.5px solid #FFD54F",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#F57F17",
                            }}
                          >
                            NKDA —{" "}
                            {typeof v.nkda === "string"
                              ? v.nkda
                              : "No Known Drug Allergies"}
                          </Typography>
                        </Box>
                      )}
                      {v.items
                        ?.filter((item) => item.agent)
                        .map((item, i) => (
                          <Box
                            key={i}
                            sx={{
                              mb: 1.5,
                              border: "1px solid #E1E1E1",
                              borderRadius: "8px",
                              p: 1.5,
                              backgroundColor: "#FAFCFC",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: "#333",
                                }}
                              >
                                {item.agent}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                {item.type_of_allergy && (
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.3,
                                      borderRadius: "4px",
                                      backgroundColor: "#E3F2FD",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: "#1565C0",
                                    }}
                                  >
                                    {item.type_of_allergy}
                                  </Box>
                                )}
                                {item.severity && (
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.3,
                                      borderRadius: "4px",
                                      backgroundColor: "#FCE4EC",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: "#C62828",
                                    }}
                                  >
                                    {item.severity}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                              }}
                            >
                              {item.reactions && (
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#666",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Reactions
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "14px", color: "#333" }}
                                  >
                                    {item.reactions}
                                  </Typography>
                                </Box>
                              )}
                              {item.start_date && (
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#666",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Start Date
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "14px", color: "#333" }}
                                  >
                                    {item.start_date}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            {item.comments && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: "#666",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Comments
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "14px", color: "#555" }}
                                >
                                  {item.comments}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "past_medical_hx" &&
                (() => {
                  const v = viewData.value;
                  const transItems = viewData.trans?.items || [];
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#0097B2",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                          }}
                        >
                          No Past Medical History
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.3,
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontWeight: 600,
                            backgroundColor:
                              v.no_past_medical_hx === "1"
                                ? "#E8F5E9"
                                : "#FFF8E1",
                            color:
                              v.no_past_medical_hx === "1"
                                ? "#2E7D32"
                                : "#F57F17",
                            border: `1px solid ${v.no_past_medical_hx === "1" ? "#A5D6A7" : "#FFD54F"}`,
                          }}
                        >
                          {v.no_past_medical_hx === "1" ? "Yes" : "No"}
                        </Box>
                      </Box>
                      {v.no_past_medical_hx !== "1" && v.diseases && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              pb: 0.4,
                              mb: 0.8,
                              borderBottom: "1.5px solid #E1E1E1",
                            }}
                          >
                            Diseases
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              mt: 0.5,
                            }}
                          >
                            {v.diseases
                              .split(",")
                              .map((d) => d.trim())
                              .filter(Boolean)
                              .map((d) => (
                                <Box
                                  key={d}
                                  sx={{
                                    px: 1.2,
                                    py: 0.4,
                                    borderRadius: "20px",
                                    border: "1.5px solid #0097B2",
                                    backgroundColor: "#E6F7FA",
                                    fontSize: "14px",
                                    color: "#0097B2",
                                    fontWeight: 500,
                                  }}
                                >
                                  {d}
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      )}
                      {v.comments && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              pb: 0.4,
                              mb: 0.8,
                              borderBottom: "1.5px solid #E1E1E1",
                            }}
                          >
                            Comments
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#555",
                              lineHeight: 1.6,
                            }}
                          >
                            {v.comments}
                          </Typography>
                        </Box>
                      )}
                      {transItems.length > 0 && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              pb: 0.4,
                              mb: 1,
                              borderBottom: "1.5px solid #E1E1E1",
                            }}
                          >
                            Disease Details
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {transItems.map((item, i) => (
                              <Box
                                key={i}
                                sx={{
                                  p: 1.2,
                                  border: "1px solid #E1E1E1",
                                  borderRadius: "8px",
                                  backgroundColor: "#FAFCFC",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    color: "#333",
                                    mb: 0.4,
                                  }}
                                >
                                  {item.disease_name}
                                </Typography>
                                {item.disease_comments && (
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: "#555",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {item.disease_comments}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "past_surgical_hx" &&
                (() => {
                  const v = viewData.value;
                  const items = v.items?.filter((item) => item.procedure) || [];
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#0097B2",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                          }}
                        >
                          No Past Surgical History
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.3,
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontWeight: 600,
                            backgroundColor:
                              items.length === 0 ? "#E8F5E9" : "#FFF8E1",
                            color: items.length === 0 ? "#2E7D32" : "#F57F17",
                            border: `1px solid ${items.length === 0 ? "#A5D6A7" : "#FFD54F"}`,
                          }}
                        >
                          {items.length === 0 ? "Yes" : "No"}
                        </Box>
                      </Box>
                      {items.length > 0 && (
                        <TableContainer
                          sx={{
                            border: "1px solid #E1E1E1",
                            borderRadius: "8px",
                          }}
                        >
                          <Table size="small" sx={{ tableLayout: "fixed" }}>
                            <colgroup>
                              <col style={{ width: "24%" }} />
                              <col style={{ width: "21%" }} />
                              <col style={{ width: "24%" }} />
                              <col style={{ width: "30%" }} />
                            </colgroup>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: "#F5F7F8" }}>
                                {[
                                  "Procedure",
                                  "Body Site",
                                  "Findings",
                                  "Date",
                                ].map((h, i) => (
                                  <TableCell
                                    key={h}
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#0097B2",
                                      textTransform: "uppercase",
                                      py: "7px",
                                      borderRight:
                                        i < 3 ? "1px solid #E1E1E1" : "none",
                                    }}
                                  >
                                    {h}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {items.map((item, i) => (
                                <TableRow
                                  key={i}
                                  sx={{
                                    backgroundColor:
                                      i % 2 === 0 ? "#FAFAFA" : "white",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      borderRight: "1px solid #E1E1E1",
                                      fontSize: "14px",
                                      color: "#333",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {item.procedure}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      borderRight: "1px solid #E1E1E1",
                                      fontSize: "14px",
                                      color: "#555",
                                    }}
                                  >
                                    {item.body_site || "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      borderRight: "1px solid #E1E1E1",
                                      fontSize: "14px",
                                      color: "#555",
                                    }}
                                  >
                                    {item.findings || "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      fontSize: "14px",
                                      color: "#555",
                                    }}
                                  >
                                    {item.procedure_date || "—"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "past_diagnostic_hx" &&
                (() => {
                  const v = viewData.value;
                  const items = v.items?.filter((item) => item.procedure) || [];
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#0097B2",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                          }}
                        >
                          No Past Diagnostic History
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.3,
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontWeight: 600,
                            backgroundColor:
                              items.length === 0 ? "#E8F5E9" : "#FFF8E1",
                            color: items.length === 0 ? "#2E7D32" : "#F57F17",
                            border: `1px solid ${items.length === 0 ? "#A5D6A7" : "#FFD54F"}`,
                          }}
                        >
                          {items.length === 0 ? "Yes" : "No"}
                        </Box>
                      </Box>
                      {items.length > 0 && (
                        <TableContainer
                          sx={{
                            border: "1px solid #E1E1E1",
                            borderRadius: "8px",
                          }}
                        >
                          <Table size="small" sx={{ tableLayout: "fixed" }}>
                            <colgroup>
                              <col style={{ width: "24%" }} />
                              <col style={{ width: "21%" }} />
                              <col style={{ width: "24%" }} />
                              <col style={{ width: "30%" }} />
                            </colgroup>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: "#F5F7F8" }}>
                                {[
                                  "Procedure",
                                  "Body Site",
                                  "Findings",
                                  "Date",
                                ].map((h, i) => (
                                  <TableCell
                                    key={h}
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#0097B2",
                                      textTransform: "uppercase",
                                      py: "7px",
                                      borderRight:
                                        i < 3 ? "1px solid #E1E1E1" : "none",
                                    }}
                                  >
                                    {h}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {items.map((item, i) => (
                                <TableRow
                                  key={i}
                                  sx={{
                                    backgroundColor:
                                      i % 2 === 0 ? "#FAFAFA" : "white",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      borderRight: "1px solid #E1E1E1",
                                      fontSize: "14px",
                                      color: "#333",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {item.procedure}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      borderRight: "1px solid #E1E1E1",
                                      fontSize: "14px",
                                      color: "#555",
                                    }}
                                  >
                                    {item.body_site || "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      borderRight: "1px solid #E1E1E1",
                                      fontSize: "14px",
                                      color: "#555",
                                    }}
                                  >
                                    {item.findings || "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      p: "6px 8px",
                                      fontSize: "14px",
                                      color: "#555",
                                    }}
                                  >
                                    {item.procedure_date || "—"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "gyn_hx" &&
                (() => {
                  const v = viewData.value;
                  const formatValue = (val) =>
                    val === true ? "Yes" : val === false ? "No" : val || "—";
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        {v.lmp && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#0097B2",
                                textTransform: "uppercase",
                              }}
                            >
                              LMP
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                fontWeight: 500,
                              }}
                            >
                              {v.lmp}
                            </Typography>
                          </Box>
                        )}
                        {v.hysterectomy && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#0097B2",
                                textTransform: "uppercase",
                              }}
                            >
                              Hysterectomy
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                fontWeight: 500,
                              }}
                            >
                              {v.hysterectomy}
                            </Typography>
                          </Box>
                        )}
                        {v.menopause && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#0097B2",
                                textTransform: "uppercase",
                              }}
                            >
                              Menopause
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                fontWeight: 500,
                              }}
                            >
                              {v.menopause}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                            }}
                          >
                            Abnormal Pap
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            {formatValue(v.abnormal_pap)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                            }}
                          >
                            Total Pregnancies
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            {v.total_pregnancies || "0"}
                          </Typography>
                        </Box>
                      </Box>

                      {v.treatment && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 0.8,
                            }}
                          >
                            Treatment
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#555",
                              lineHeight: 1.5,
                            }}
                          >
                            {v.treatment}
                          </Typography>
                        </Box>
                      )}

                      {v.menstrual_history && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 1,
                            }}
                          >
                            Menstrual History
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              gap: "12px 24px",
                              p: 1.5,
                              backgroundColor: "#FAFCFC",
                              border: "1px solid #E1E1E1",
                              borderRadius: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666",
                                  fontWeight: 500,
                                }}
                              >
                                Menarche:
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                  fontWeight: 600,
                                }}
                              >
                                {v.menstrual_history.menarche || "—"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666",
                                  fontWeight: 500,
                                }}
                              >
                                Cycle:
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                  fontWeight: 600,
                                }}
                              >
                                {v.menstrual_history.cycle || "—"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666",
                                  fontWeight: 500,
                                }}
                              >
                                Cycle Duration:
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                  fontWeight: 600,
                                }}
                              >
                                {v.menstrual_history.cycle_duration || "—"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666",
                                  fontWeight: 500,
                                }}
                              >
                                Flow:
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                  fontWeight: 600,
                                }}
                              >
                                {v.menstrual_history.flow || "—"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                gridColumn: { sm: "1 / -1" },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666",
                                  fontWeight: 500,
                                }}
                              >
                                Cramps:
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                  fontWeight: 600,
                                }}
                              >
                                {v.menstrual_history.cramps || "—"}
                              </Typography>
                            </Box>
                            {v.menstrual_history.menstrual_problems && (
                              <Box
                                sx={{
                                  gridColumn: "1 / -1",
                                  mt: 1,
                                  pt: 1,
                                  borderTop: "1px dashed #E1E1E1",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#666",
                                    fontWeight: 600,
                                    mb: 0.5,
                                  }}
                                >
                                  Menstrual Problems:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#555",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {v.menstrual_history.menstrual_problems}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                      {v.birth_control && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 1,
                            }}
                          >
                            Birth Control
                          </Typography>
                          {v.birth_control.none ? (
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#C53030",
                                fontWeight: 600,
                                px: 1.5,
                                py: 0.8,
                                backgroundColor: "#FFF5F5",
                                border: "1px solid #FEB2B2",
                                borderRadius: "6px",
                                display: "inline-block",
                              }}
                            >
                              None Used
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.2,
                              }}
                            >
                              {Object.entries(v.birth_control).map(
                                ([key, val]) => {
                                  if (
                                    !val ||
                                    [
                                      "none",
                                      "Insert Date",
                                      "Removal Date",
                                    ].includes(key)
                                  )
                                    return null;

                                  const labelMap = {
                                    pills: "Pills",
                                    condoms: "Condoms",
                                    btl: "BTL",
                                    contraceptive_ring: "Contraceptive Ring",
                                    vasectomy: "Vasectomy",
                                    implanon: "Implanon",
                                    attempting_pregnancy:
                                      "Attempting Pregnancy",
                                    iud: "IUD",
                                    others: "Others",
                                  };
                                  const displayLabel =
                                    labelMap[key] || key.replace(/_/g, " ");

                                  let details = "";
                                  if (typeof val === "string") {
                                    details = val;
                                  }
                                  if (key === "iud") {
                                    const parts = [];
                                    if (typeof val === "string")
                                      parts.push(val);
                                    if (v.birth_control["Insert Date"])
                                      parts.push(
                                        `Inserted: ${v.birth_control["Insert Date"]}`,
                                      );
                                    if (v.birth_control["Removal Date"])
                                      parts.push(
                                        `Removal: ${v.birth_control["Removal Date"]}`,
                                      );
                                    if (parts.length)
                                      details = parts.join(" | ");
                                  }

                                  return (
                                    <Box
                                      key={key}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        p: 1.2,
                                        backgroundColor: "#FAFCFC",
                                        borderRadius: "8px",
                                        border: "1px solid #E1E1E1",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          backgroundColor: "#0097B2",
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          color: "#2D3748",
                                        }}
                                      >
                                        {displayLabel}
                                      </Typography>
                                      {details && (
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "#666",
                                            ml: 1,
                                            fontStyle: "italic",
                                          }}
                                        >
                                          ({details})
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                },
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })()}

              {TAB_KEYS[activeTab] === "past_pregnancies_hx" &&
                (() => {
                  const v = viewData.value;
                  const pregs = v.items || [];
                  if (pregs.length === 0) {
                    return (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No past pregnancy history recorded.
                      </Typography>
                    );
                  }
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {pregs.map((p, i) => (
                        <Box
                          key={i}
                          sx={{
                            border: "1px solid #E1E1E1",
                            borderRadius: "8px",
                            p: 1.5,
                            backgroundColor: "#FAFCFC",
                          }}
                        >
                          {p.date && (
                            <Box
                              sx={{
                                borderBottom: "1px solid #E1E1E1",
                                pb: 0.6,
                                mb: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#0097B2",
                                }}
                              >
                                Date: {p.date}
                              </Typography>
                            </Box>
                          )}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px 16px",
                            }}
                          >
                            {p.ga_weeks && (
                              <Box>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#777",
                                    display: "block",
                                  }}
                                >
                                  GA Weeks
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#333",
                                    fontWeight: 600,
                                  }}
                                >
                                  {p.ga_weeks}
                                </span>
                              </Box>
                            )}
                            {p.birth_weight && (
                              <Box>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#777",
                                    display: "block",
                                  }}
                                >
                                  Birth Weight
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#333",
                                    fontWeight: 600,
                                  }}
                                >
                                  {p.birth_weight}
                                </span>
                              </Box>
                            )}
                            {p.type_of_delivery && (
                              <Box>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#777",
                                    display: "block",
                                  }}
                                >
                                  Delivery Type
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#333",
                                    fontWeight: 600,
                                  }}
                                >
                                  {p.type_of_delivery}
                                </span>
                              </Box>
                            )}
                            {p.place_of_delivery && (
                              <Box>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#777",
                                    display: "block",
                                  }}
                                >
                                  Delivery Place
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#333",
                                    fontWeight: 600,
                                  }}
                                >
                                  {p.place_of_delivery}
                                </span>
                              </Box>
                            )}
                            {p.preterm_labor && (
                              <Box>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#777",
                                    display: "block",
                                  }}
                                >
                                  Preterm Labor
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#333",
                                    fontWeight: 600,
                                  }}
                                >
                                  {p.preterm_labor}
                                </span>
                              </Box>
                            )}
                            {p.outcome && (
                              <Box>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#777",
                                    display: "block",
                                  }}
                                >
                                  Outcome
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#333",
                                    fontWeight: 600,
                                  }}
                                >
                                  {p.outcome}
                                </span>
                              </Box>
                            )}
                          </Box>
                          {p.comments && (
                            <Box
                              sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: "1px dashed #E1E1E1",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#777",
                                  display: "block",
                                }}
                              >
                                Comments
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#555",
                                  lineHeight: 1.4,
                                }}
                              >
                                {p.comments}
                              </span>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  );
                })()}

              {TAB_KEYS[activeTab] === "family_history" &&
                (() => {
                  const v = viewData.value;
                  if (v.no_family_illness) {
                    return (
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#2E7D32",
                            fontWeight: 600,
                            p: 1.5,
                            backgroundColor: "#E8F5E9",
                            borderRadius: "8px",
                            border: "1px solid #A5D6A7",
                          }}
                        >
                          No known family illness history.
                        </Typography>
                        {v.comments && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#0097B2",
                                textTransform: "uppercase",
                                borderBottom: "1.5px solid #E1E1E1",
                                pb: 0.4,
                                mb: 0.8,
                              }}
                            >
                              Comments
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#555" }}
                            >
                              {v.comments}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  const renderMember = (relation, data) => {
                    if (!data) return null;
                    const diseases = data.diseases || [];
                    return (
                      <Box
                        key={relation}
                        sx={{
                          mb: 1.5,
                          p: 1.5,
                          backgroundColor: "#FAFCFC",
                          borderRadius: "8px",
                          border: "1px solid #E1E1E1",
                        }}
                      >
                        {/* Header: name + age + living status */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                            pb: 0.8,
                            borderBottom: "1px solid #E1E1E1",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#333",
                            }}
                          >
                            {relation}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1.5 }}>
                            {data.age && (
                              <Typography
                                sx={{ fontSize: "14px", color: "#666" }}
                              >
                                Age: <strong>{data.age}</strong>
                              </Typography>
                            )}
                            {data.living_status && (
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.2,
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  backgroundColor:
                                    data.living_status === "Living"
                                      ? "#E8F5E9"
                                      : data.living_status === "Deceased"
                                        ? "#FFEBEE"
                                        : "#F3F5F6",
                                  color:
                                    data.living_status === "Living"
                                      ? "#2E7D32"
                                      : data.living_status === "Deceased"
                                        ? "#C62828"
                                        : "#555",
                                }}
                              >
                                {data.living_status}
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {/* Diseases */}
                        {diseases.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}
                          >
                            {diseases.map((dis) => (
                              <Box
                                key={dis}
                                sx={{
                                  px: 1.2,
                                  py: 0.4,
                                  borderRadius: "12px",
                                  backgroundColor: "#E6F7FA",
                                  border: "1.5px solid #0097B2",
                                  fontSize: "14px",
                                  color: "#0097B2",
                                  fontWeight: 500,
                                }}
                              >
                                {dis}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#999",
                              fontStyle: "italic",
                            }}
                          >
                            No illnesses reported.
                          </Typography>
                        )}
                        {/* Malignancy */}
                        {data.malignancy && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#666",
                                textTransform: "uppercase",
                              }}
                            >
                              Malignancy
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#333" }}
                            >
                              {data.malignancy}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  };

                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {renderMember("Father", v.father)}
                      {renderMember("Mother", v.mother)}
                      {v.members?.map((m, idx) =>
                        renderMember(m.relation || `Member ${idx + 1}`, m),
                      )}
                      {v.comments && (
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 0.8,
                            }}
                          >
                            Comments
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#555",
                              lineHeight: 1.5,
                            }}
                          >
                            {v.comments}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

              {TAB_KEYS[activeTab] === "social_hx" &&
                (() => {
                  const v = viewData.value;
                  const Field = ({ label, value }) =>
                    value ? (
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#0097B2",
                            textTransform: "uppercase",
                            mb: 0.3,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: 500,
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    ) : null;
                  const SubstanceCard = ({
                    title,
                    status,
                    ifYes,
                    qty,
                    duration,
                  }) =>
                    status ? (
                      <Box
                        sx={{
                          p: 1.2,
                          backgroundColor: "#FAFCFC",
                          borderRadius: "8px",
                          border: "1px solid #E1E1E1",
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#666",
                            textTransform: "uppercase",
                            mb: 0.5,
                          }}
                        >
                          {title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: 600,
                          }}
                        >
                          {status.replace(/_/g, " ")}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mt: 0.5,
                          }}
                        >
                          {ifYes && (
                            <Typography
                              sx={{ fontSize: "14px", color: "#555" }}
                            >
                              If Yes: <strong>{ifYes}</strong>
                            </Typography>
                          )}
                          {qty && (
                            <Typography
                              sx={{ fontSize: "14px", color: "#555" }}
                            >
                              Qty: <strong>{qty}</strong>
                            </Typography>
                          )}
                          {duration && (
                            <Typography
                              sx={{ fontSize: "14px", color: "#555" }}
                            >
                              Duration: <strong>{duration}</strong>
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : null;
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* General info grid */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "12px 24px",
                        }}
                      >
                        <Field label="Marital Status" value={v.maritalstatus} />
                        <Field label="Occupation" value={v.occupation} />
                        <Field label="Industry" value={v.industry} />
                        <Field
                          label="General Stress Level"
                          value={v.generalstresslevel}
                        />
                        <Field label="Diet" value={v.diet} />
                        <Field
                          label="Caffeine Intake"
                          value={v.caffeineintake}
                        />
                      </Box>
                      {/* Substance use */}
                      {(v.tobacco_status ||
                        v.chewing_status ||
                        v.alcohol ||
                        v.drugs) && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 1,
                            }}
                          >
                            Substance Use
                          </Typography>
                          <SubstanceCard
                            title="Tobacco — Smoking"
                            status={v.tobacco_status}
                            qty={v.tobacco_quan}
                            duration={v.tobacco_quit_duration}
                          />
                          <SubstanceCard
                            title="Tobacco — Chewing"
                            status={v.chewing_status}
                            qty={v.chewing_quan}
                            duration={v.chewing_quit_duration}
                          />
                          <SubstanceCard
                            title="Alcohol"
                            status={v.alcohol}
                            ifYes={v.alcohol_yes}
                            duration={v.alcohol_quit_duration}
                          />
                          <SubstanceCard
                            title="Drugs"
                            status={v.drugs}
                            ifYes={v.drugs_yes}
                            duration={v.drugs_quit_duration}
                          />
                        </Box>
                      )}
                      {v.excercise && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 0.6,
                            }}
                          >
                            Exercise
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#555" }}>
                            {v.excercise}
                          </Typography>
                        </Box>
                      )}
                      {v.comments && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 0.6,
                            }}
                          >
                            Comments
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#555" }}>
                            {v.comments}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

              {TAB_KEYS[activeTab] === "hpi" &&
                (() => {
                  const hpi = viewData.hpi;
                  const hpiTrans = viewData.hpi_trans;
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* HPI — illness description */}
                      {hpi?.illness_desc && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 0.8,
                            }}
                          >
                            History of Present Illness
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              lineHeight: 1.7,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {hpi.illness_desc}
                          </Typography>
                        </Box>
                      )}
                      {/* HPI Trans — illness items grouped by illness */}
                      {hpiTrans?.items?.length > 0 &&
                        (() => {
                          const grouped = hpiTrans.items.reduce((acc, item) => {
                            const key = item.illness || "General";
                            if (!acc[key]) acc[key] = [];
                            if (item.options || item.comments)
                              acc[key].push(item);
                            return acc;
                          }, {});
                          return (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: 700,
                                  color: "#0097B2",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.6px",
                                  borderBottom: "1.5px solid #E1E1E1",
                                  pb: 0.4,
                                  mb: 1,
                                }}
                              >
                                HPI Details
                              </Typography>
                              {Object.entries(grouped).map(
                                ([illness, items]) => (
                                  <Box
                                    key={illness}
                                    sx={{
                                      mb: 1.5,
                                      p: 1.5,
                                      border: "1px solid #E1E1E1",
                                      borderRadius: "8px",
                                      backgroundColor: "#FAFCFC",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 700,
                                        color: "#333",
                                        mb: 1,
                                        pb: 0.5,
                                        borderBottom: "1px solid #F0F0F0",
                                      }}
                                    >
                                      {illness}
                                    </Typography>
                                    {items.map((item, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          mb: 0.6,
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        {item.options && (
                                          <Box
                                            sx={{
                                              px: 1,
                                              py: 0.3,
                                              borderRadius: "4px",
                                              backgroundColor: "#E6F7FA",
                                              border: "1px solid #0097B2",
                                              fontSize: "14px",
                                              color: "#0097B2",
                                              fontWeight: 600,
                                              flexShrink: 0,
                                            }}
                                          >
                                            {item.options.trim()}
                                          </Box>
                                        )}
                                        {item.comments && (
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              color: "#555",
                                              mt: "2px",
                                            }}
                                          >
                                            {item.comments}
                                          </Typography>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                ),
                              )}
                            </Box>
                          );
                        })()}
                      {!hpi?.illness_desc && !hpiTrans?.items?.length && (
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#999",
                            textAlign: "center",
                            py: 2,
                          }}
                        >
                          No HPI data recorded yet.
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "ros" &&
                (() => {
                  const items = viewData?.value?.items || [];
                  if (!items.length)
                    return (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No ROS data recorded yet.
                      </Typography>
                    );
                  const statusColor = (name) => {
                    if (!name)
                      return {
                        bg: "#F3F5F6",
                        color: "#888",
                        border: "#D0D8DA",
                      };
                    if (name === "Normal")
                      return {
                        bg: "#E8F5E9",
                        color: "#2E7D32",
                        border: "#A5D6A7",
                      };
                    if (name === "Abnormal")
                      return {
                        bg: "#FFEBEE",
                        color: "#C62828",
                        border: "#EF9A9A",
                      };
                    return {
                      bg: "#FFF8E1",
                      color: "#F57F17",
                      border: "#FFD54F",
                    };
                  };
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {items.map((item, i) => {
                        const colors = statusColor(item.ros_param_name);
                        return (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                              p: "8px 12px",
                              borderRadius: "8px",
                              border: "1px solid #E1E1E1",
                              backgroundColor:
                                i % 2 === 0 ? "#FAFCFC" : "white",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#333",
                                minWidth: 180,
                                pt: "2px",
                              }}
                            >
                              {item.ros_param}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                flex: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  backgroundColor: colors.bg,
                                  color: colors.color,
                                  border: `1px solid ${colors.border}`,
                                  flexShrink: 0,
                                }}
                              >
                                {item.ros_param_name || "Not Assessed"}
                              </Box>
                              {item.ros_param_comments && (
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#555",
                                    lineHeight: 1.5,
                                    flex: 1,
                                  }}
                                >
                                  {item.ros_param_comments.trim()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "impression_plan" &&
                (() => {
                  const v = viewData?.value;
                  if (!v)
                    return (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No data recorded yet.
                      </Typography>
                    );
                  const Block = ({ label, content }) =>
                    content ? (
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#0097B2",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            borderBottom: "1.5px solid #E1E1E1",
                            pb: 0.4,
                            mb: 0.8,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            lineHeight: 1.7,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {content}
                        </Typography>
                      </Box>
                    ) : null;
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Block label="Impression" content={v.impression} />
                      <Block label="Plan" content={v.plan} />
                      {(v.return_in_value || v.return_in_option) && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.8,
                            backgroundColor: "#E6F7FA",
                            border: "1.5px solid #0097B2",
                            borderRadius: "8px",
                            alignSelf: "flex-start",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                            }}
                          >
                            Return In
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#333",
                            }}
                          >
                            {v.return_in_value} {v.return_in_option}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "instructions" &&
                (() => {
                  const v = viewData?.value;
                  if (!v?.instructions && !v?.comments)
                    return (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No instructions recorded yet.
                      </Typography>
                    );
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {v.instructions && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 1,
                            }}
                          >
                            Instructions
                          </Typography>
                          <Box
                            sx={{
                              backgroundColor: "#FAFCFC",
                              border: "1px solid #E1E1E1",
                              borderRadius: "8px",
                              p: 1.5,
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.8,
                            }}
                          >
                            {v.instructions
                              .split("*")
                              .map((line) => line.trim())
                              .filter(Boolean)
                              .map((line, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: "#0097B2",
                                      fontWeight: 700,
                                      flexShrink: 0,
                                      mt: "1px",
                                    }}
                                  >
                                    •
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: "#333",
                                      lineHeight: 1.7,
                                    }}
                                  >
                                    {line}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      )}
                      {v.comments && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 0.8,
                            }}
                          >
                            Comments
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#555",
                              lineHeight: 1.6,
                            }}
                          >
                            {v.comments}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "referrals" &&
                (() => {
                  const items = viewData?.value?.items || [];
                  if (!items.length)
                    return (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No referrals recorded yet.
                      </Typography>
                    );
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {items.map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            p: 1.5,
                            border: "1px solid #E1E1E1",
                            borderRadius: "8px",
                            backgroundColor: "#FAFCFC",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                              pb: 0.8,
                              borderBottom: "1px solid #F0F0F0",
                            }}
                          >
                            <Box
                              sx={{
                                px: 1,
                                py: 0.3,
                                borderRadius: "4px",
                                fontSize: "14px",
                                fontWeight: 700,
                                backgroundColor:
                                  item.referral_type === "Internal"
                                    ? "#E3F2FD"
                                    : "#FFF8E1",
                                color:
                                  item.referral_type === "Internal"
                                    ? "#1565C0"
                                    : "#F57F17",
                                border: `1px solid ${item.referral_type === "Internal" ? "#90CAF9" : "#FFD54F"}`,
                              }}
                            >
                              {item.referral_type || "—"}
                            </Box>
                            {item.referral_date && (
                              <Typography
                                sx={{ fontSize: "14px", color: "#888" }}
                              >
                                {item.referral_date.split(" ")[0]}
                              </Typography>
                            )}
                          </Box>
                          {item.reason && (
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                lineHeight: 1.6,
                              }}
                            >
                              {item.reason}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              {TAB_KEYS[activeTab] === "vitals" &&
                (() => {
                  const a = appointmentData;
                  if (!a)
                    return (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No vitals data available.
                      </Typography>
                    );
                  const VField = ({ label, value, unit }) =>
                    value !== undefined && value !== null && value !== "" ? (
                      <Box
                        sx={{
                          p: 1.2,
                          border: "1px solid #E1E1E1",
                          borderRadius: "8px",
                          backgroundColor: "#FAFCFC",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#0097B2",
                            textTransform: "uppercase",
                            mb: 0.3,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "#333",
                          }}
                        >
                          {String(value)}
                          {unit ? (
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#888",
                                fontWeight: 400,
                                marginLeft: 4,
                              }}
                            >
                              {unit}
                            </span>
                          ) : null}
                        </Typography>
                      </Box>
                    ) : null;
                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 1.5,
                        }}
                      >
                        <VField
                          label="BP Systolic"
                          value={a.bp_s}
                          unit="mmHg"
                        />
                        <VField
                          label="BP Diastolic"
                          value={a.bp_d}
                          unit="mmHg"
                        />
                        <VField label="Temperature" value={a.fever} unit="°F" />
                        <VField label="Height" value={a.height} unit="cm" />
                        <VField label="Weight" value={a.weight} unit="kg" />
                        <VField label="BMI" value={a.bmi} />
                        <VField
                          label="Pulse Rate"
                          value={a.pulse_rate}
                          unit="bpm"
                        />
                        <VField
                          label="Heart Rate"
                          value={a.heart_rate}
                          unit="bpm"
                        />
                        <VField label="SpO2" value={a.spo2} unit="%" />
                        <VField
                          label="Pain Level"
                          value={a.pain_level}
                          unit="/10"
                        />
                      </Box>
                      {(a.alcohol !== undefined ||
                        a.tobacco !== undefined ||
                        a.smoking !== undefined) && (
                        <Box sx={{ display: "flex", gap: 2 }}>
                          {[
                            { label: "Alcohol", value: a.alcohol },
                            { label: "Tobacco", value: a.tobacco },
                            { label: "Smoking", value: a.smoking },
                          ].map(({ label, value }) => (
                            <Box
                              key={label}
                              sx={{
                                px: 1.2,
                                py: 0.5,
                                borderRadius: "6px",
                                border: `1.5px solid ${value ? "#C62828" : "#A5D6A7"}`,
                                backgroundColor: value ? "#FFEBEE" : "#E8F5E9",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: value ? "#C62828" : "#2E7D32",
                                }}
                              >
                                {label}: {value ? "Yes" : "No"}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {a.comments && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#0097B2",
                              textTransform: "uppercase",
                              borderBottom: "1.5px solid #E1E1E1",
                              pb: 0.4,
                              mb: 0.6,
                            }}
                          >
                            Comments
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#555",
                              lineHeight: 1.6,
                            }}
                          >
                            {a.comments}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: "14px", color: "#999" }}>
                No data recorded yet.
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ClinicalDataTabs;
